import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth, storage } from './firebase';
import type { Customer, Invoice, CompanyProfile, Transaction, UserRole, Product, Category, User, FirebaseUser, InvoiceActivity, LineItem, PaymentMethod } from '@/types/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
  DocumentData,
  QueryDocumentSnapshot,
  limit,
  setDoc,
  documentId,
  writeBatch
} from 'firebase/firestore';
import { generatePaymentKey, getTodayInLocalTimezone } from './utils';
import { createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { Role, 
  // Unused types - commented out but kept for reference
  // RolePermissions, 
  // FeatureAccess 
} from '@/types/firebase';
import { formatCurrency, formatDate } from './utils';
import { Transaction as FirebaseTransaction } from '@/types/firebase';
import { getApiBaseUrl } from '@/lib/environment';
import { format } from 'date-fns';

const logApiError = (context: string, error: unknown): void => {
  console.error(`[api] ${context}`, error);
};

// Add this near the top of the file, after imports
const getBaseUrl = () => {
  if (typeof window === 'undefined') {
    // Server-side
    return import.meta.env.VITE_API_URL || '';
  }
  // Client-side
  return window.location.origin;
};

// Helper function to convert Firestore data to our types
function convertTimestamps<T extends DocumentData>(doc: QueryDocumentSnapshot<DocumentData>): T & { id: string } {
  const data = doc.data();
  Object.keys(data).forEach(key => {
    if (data[key] instanceof Timestamp) {
      data[key] = data[key].toDate();
    }
  });
  return { id: doc.id, ...data } as T & { id: string };
}

// Helper function to get company ID - checks for impersonation first
async function getCompanyId(firebaseUser: FirebaseUser): Promise<string> {
  // Check if we're in impersonation mode
  const IMPERSONATION_KEY = 'impersonation_data';
  const TARGET_USER_KEY = 'impersonation_target_user';

  console.log('[getCompanyId] Called for user:', firebaseUser.uid);
  console.log('[getCompanyId] Checking localStorage...');

  const impersonationData = typeof window !== 'undefined' ? localStorage.getItem(IMPERSONATION_KEY) : null;
  const targetUserData = typeof window !== 'undefined' ? localStorage.getItem(TARGET_USER_KEY) : null;

  console.log('[getCompanyId] Impersonation data exists:', !!impersonationData);
  console.log('[getCompanyId] Target user data exists:', !!targetUserData);

  if (impersonationData && targetUserData) {
    try {
      const impData = JSON.parse(impersonationData);
      const targetUser = JSON.parse(targetUserData);

      // Check if session has expired
      const now = Date.now();
      const expiresAt = impData.expiresAt.seconds * 1000;

      if (now < expiresAt) {
        // Session is still valid, use impersonated company ID
        console.log('[getCompanyId] Using impersonated companyId:', targetUser.companyId);
        return targetUser.companyId;
      } else {
        // Session expired, clear it
        console.log('[getCompanyId] Impersonation session expired, clearing...');
        localStorage.removeItem(IMPERSONATION_KEY);
        localStorage.removeItem(TARGET_USER_KEY);
      }
    } catch (error) {
      console.error('[getCompanyId] Error parsing impersonation data:', error);
      localStorage.removeItem(IMPERSONATION_KEY);
      localStorage.removeItem(TARGET_USER_KEY);
    }
  }

  // Not impersonating, use the actual user's company ID
  const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
  const userData = userDoc.exists() ? userDoc.data() as User : null;
  const companyId = userData?.companyId || `company_${firebaseUser.uid}`;
  console.log('[getCompanyId] Using actual user companyId:', companyId);
  return companyId;
}

// Customers
export async function getCustomers(companyId?: string) {
  try {
    let targetCompanyId = companyId;

    if (!targetCompanyId) {
      const user = auth.currentUser as FirebaseUser | null;
      if (!user) throw new Error('Not authenticated');
      // Get company ID (handles impersonation)
      targetCompanyId = await getCompanyId(user);
    }

    // All users (including super_admin) can only see customers from their company
    const customersQuery = query(
      collection(db, 'customers'),
      where('companyId', '==', targetCompanyId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(customersQuery);

    return querySnapshot.docs.map(doc => ({
      ...convertTimestamps<Customer>(doc)
    }));
  } catch (error) {
    // Return empty array instead of throwing to handle gracefully in UI
    logApiError('getCustomers', error);
    return [];
  }
}

export async function getCustomer(id: string) {
  try {
    const docRef = doc(db, 'customers', id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return convertTimestamps<Customer>(docSnap);
    }
    throw new Error('Customer not found');
  } catch (error) {
    logApiError('getCustomer', error);
    throw new Error('Failed to fetch customer details. Please try again.');
  }
}

export async function createCustomer(
  customer: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>,
  companyId: string
) {
  // Ensure all required fields are present (matching validateCustomerData in firestore.rules)
  const customerData = {
    ...customer,
    companyId,
    // Ensure type is one of the allowed values
    type: customer.type || 'Owner',
    // Ensure status is one of the allowed values
    status: customer.status || 'active',
    // Use serverTimestamp for Firestore rules validation
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  
  const docRef = await addDoc(collection(db, 'customers'), customerData);
  
  // Return the created customer with the generated ID
  return { 
    id: docRef.id, 
    ...customer,
    companyId
  };
}

export async function updateCustomer(companyId: string, customerId: string, customer: Partial<Customer>) {
  const updateData = customer;
  const customerRef = doc(collection(db, 'customers'), customerId);
  await updateDoc(customerRef, {
    ...updateData,
    companyId,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteCustomer(id: string) {
  try {
    // First check if the customer exists
    const customerRef = doc(db, 'customers', id);
    const customerDoc = await getDoc(customerRef);
    
    if (!customerDoc.exists()) {
      throw new Error('Customer not found');
    }

    // Check for existing invoices
    const invoicesQuery = query(
      collection(db, 'invoices'),
      where('customerId', '==', id),
      limit(1)
    );
    const invoicesSnapshot = await getDocs(invoicesQuery);
    
    if (!invoicesSnapshot.empty) {
      throw new Error('Cannot delete customer with existing invoices. Please delete all invoices first.');
    }

    // If we get here, we can safely delete the customer
    await deleteDoc(customerRef);
    return true;
  } catch (error) {
    // 'Error deleting customer:', error);
    logApiError('deleteCustomer', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to delete customer. Please try again.');
  }
}

// Invoices
export async function getInvoices(companyId?: string) {
  try {
    let targetCompanyId = companyId;

    console.log('[getInvoices] Called with companyId param:', companyId);

    if (!targetCompanyId) {
      const user = auth.currentUser as FirebaseUser | null;
      if (!user) throw new Error('Not authenticated');
      console.log('[getInvoices] No companyId param, calling getCompanyId for user:', user.uid);
      // Get company ID (handles impersonation)
      targetCompanyId = await getCompanyId(user);
    }

    console.log('[getInvoices] Querying with companyId:', targetCompanyId);

    // All users (including super_admin) can only see invoices from their company
    const invoicesQuery = query(
      collection(db, 'invoices'),
      where('companyId', '==', targetCompanyId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(invoicesQuery);

    console.log('[getInvoices] Found', querySnapshot.docs.length, 'invoices for companyId:', targetCompanyId);

    return querySnapshot.docs.map(doc => ({
      ...convertTimestamps<Invoice>(doc)
    }));
  } catch (error) {
    // 'Error fetching invoices:', error);
    // Return empty array instead of throwing to handle gracefully in UI
    console.error('[getInvoices] Error:', error);
    logApiError('getInvoices', error);
    return [];
  }
}

export async function getInvoice(id: string) {
  try {
    // First try to get by document ID
    const docRef = doc(db, 'invoices', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return convertTimestamps<Invoice>(docSnap);
    }
    
    // If not found by ID, try to find by invoice number
    const invoicesRef = collection(db, 'invoices');
    const q = query(invoicesRef, where('invoiceNumber', '==', id), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      return convertTimestamps<Invoice>(querySnapshot.docs[0]);
    }
    
    return null;
  } catch (error) {
    // 'Error fetching invoice:', error);
    logApiError('getInvoice', error);
    throw new Error('Failed to fetch invoice. Please try again.');
  }
}

export async function createInvoice(invoice: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>) {
  try {
    // Ensure there's at least one activity for new invoices (created)
    if (!invoice.activities || invoice.activities.length === 0) {
      const currentTime = new Date().toISOString();
      invoice.activities = [{
        id: crypto.randomUUID(),
        type: 'created',
        timestamp: currentTime,
        description: 'Invoice created',
        performedBy: 'system',
        metadata: {
          invoiceNumber: invoice.invoiceNumber
        }
      }];
    }

    // Generate a secure random payment key
    const paymentKey = generatePaymentKey();

    // Prepare metadata for special invoice types
    const metadata: Record<string, any> = {};
    
    // Set metadata flags based on invoice type
    if (invoice.invoiceType === 'recurring') {
      metadata.isRecurring = true;
    } else if (invoice.invoiceType === 'installment') {
      metadata.isInstallment = true;
    }

    // Set invoice date to today in local timezone
    const today = getTodayInLocalTimezone();

    // Create the invoice data to be saved
    const invoiceData = {
      ...invoice,
      date: today, // Always use local timezone date
      paymentKey,
      metadata,
      autoPayEnabled: invoice.autoPayEnabled === true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'invoices'), invoiceData);
    
    // Get the created document to return the full invoice data
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) {
    throw new Error('Failed to create invoice');
  }
  
    // Cast properly to Invoice
    return {
      id: docSnap.id,
      ...docSnap.data()
    } as unknown as Invoice;
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw new Error('Failed to create invoice. Please try again.');
  }
}

export async function updateInvoice(id: string, invoice: Partial<Invoice>) {
  const docRef = doc(db, 'invoices', id);
  
  // Get current invoice state
  const currentInvoiceDoc = await getDoc(docRef);
  if (!currentInvoiceDoc.exists()) {
    throw new Error('Invoice not found');
  }
  
  // Handle types correctly
  interface InvoiceWithMetadata extends Invoice {
    metadata?: Record<string, any>;
  }
  
  const currentInvoice = currentInvoiceDoc.data() as InvoiceWithMetadata;
  const currentMetadata = currentInvoice.metadata || {};

  // Prevent changing status to pending if invoice has been sent
  if (invoice.status === 'pending' && 
      (currentInvoice.status === 'sent' || 
       currentInvoice.status === 'paid' || 
       currentInvoice.status === 'overdue')) {
    // `Cannot change invoice ${id} status to pending after it has been sent`);
    // Remove the status change from the update
    delete invoice.status;
  }
  
  // Update metadata based on invoice type changes
  const updatedMetadata = { ...currentMetadata };
  
  // If invoice type is being updated, update the respective flags
  if (invoice.invoiceType) {
    if (invoice.invoiceType === 'recurring') {
      updatedMetadata.isRecurring = true;
      updatedMetadata.isInstallment = false;
    } else if (invoice.invoiceType === 'installment') {
      updatedMetadata.isRecurring = false;
      updatedMetadata.isInstallment = true;
    } else if (invoice.invoiceType === 'standard') {
      updatedMetadata.isRecurring = false;
      updatedMetadata.isInstallment = false;
    }
  }
  
  // If we're changing status, add an activity for the status change
  if (invoice.status) {
    // Only add activity if status is actually changing
    const statusChanged = currentInvoice.status !== invoice.status;
    if (statusChanged) {
      const currentTime = new Date().toISOString();
      const newActivity = {
        id: crypto.randomUUID(),
        type: invoice.status as InvoiceActivity['type'], // Cast to activity type
        timestamp: currentTime,
        description: `Invoice status changed to ${invoice.status}`,
        performedBy: 'system',
        metadata: {
          previousStatus: currentInvoice.status,
          newStatus: invoice.status
        }
      };
      
      // Get existing activities or create empty array
      const activities = currentInvoice.activities || [];
      
      // Update the invoice with appended activity
      invoice.activities = [...activities, newActivity];
    }
    
    // If status is changing to 'paid', send an invoice thank you email (never for donations)
    if (invoice.status === 'paid' && currentInvoice.status !== 'paid') {
      // Donation guard: skip if this looks like a donation
      const looksLikeDonation = Boolean(
        (currentInvoice.invoiceNumber && String(currentInvoice.invoiceNumber).startsWith('DON-')) ||
        (currentInvoice as any)?.metadata?.isDonation === true
      );
      if (looksLikeDonation) {
        // Do not send payment thank-you for donations
        // Continue with status update and activity, but skip email
      } else {
      try {
        // Get company settings for email template and SendGrid API key
        const companyId = currentInvoice.companyId;
        if (companyId) {
          const companyDocRef = doc(db, 'companies', companyId);
          const companyDocSnap = await getDoc(companyDocRef);
          const company = companyDocSnap.data();
          
          // Company settings for thank you email
          
          if (company && company.emailSettings?.enableThankYouEmail) {
            // Thank you emails are enabled, preparing to send
            
            // Format the payment date
            const paymentDate = formatDate(new Date());
            
            // Prepare the email template with variables replaced
            let emailTemplate = company.emailSettings.paymentThankYou || '';
            let emailSubject = company.emailSettings.paymentThankYouSubject || 'Payment Received - Thank You';
            
            // Replace template variables
            emailTemplate = emailTemplate
              .replace(/{{customerName}}/g, currentInvoice.customerName || '')
              .replace(/{{invoiceNumber}}/g, currentInvoice.invoiceNumber || '')
              .replace(/{{invoiceAmount}}/g, formatCurrency(currentInvoice.total || 0))
              .replace(/{{paymentDate}}/g, paymentDate)
              .replace(/{{companyName}}/g, company.name || '')
              .replace(/{{paymentLink}}/g, `<a href="${getBaseUrl()}/pay/${id}" style="background-color: #22c55e; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 500;">View Invoice</a>`);
            
            emailSubject = emailSubject
              .replace(/{{invoiceNumber}}/g, currentInvoice.invoiceNumber || '')
              .replace(/{{companyName}}/g, company.name || '');
            
            // Send the thank you email
            // Import the sendThankYouEmail function
            const { sendThankYouEmail } = await import('./email');
            
            // Send the thank you email
            await sendThankYouEmail({
              invoice: { ...currentInvoice, id },
              template: emailTemplate,
              subject: emailSubject
            });
            
            // Thank you email sent successfully
          } else {
            // Thank you emails are disabled or not configured
          }
        } else {
          // No company ID found for invoice
        }
      } catch (emailError) {
        // Error sending thank you email for invoice
        logApiError('updateInvoice.sendThankYouEmail', emailError);
      }
      }
    }
  }
  
  // Update the document with all changes including updated metadata
  await updateDoc(docRef, {
    ...invoice,
    metadata: updatedMetadata,
    updatedAt: serverTimestamp()
  });
  
  // Construct and return the updated invoice
  const updatedInvoice: InvoiceWithMetadata = {
    ...currentInvoice,
    ...invoice,
    id, // Override the id to ensure it's correct
    metadata: updatedMetadata
  };
  
  return updatedInvoice as unknown as Invoice;
}

export async function deleteInvoice(id: string) {
  await deleteDoc(doc(db, 'invoices', id));
}

// Transactions
export async function getTransactions(companyId?: string) {
  try {
    let targetCompanyId = companyId;

    if (!targetCompanyId) {
      const user = auth.currentUser as FirebaseUser | null;
      if (!user) throw new Error('Not authenticated');
      // Get company ID (handles impersonation)
      targetCompanyId = await getCompanyId(user);
    }

    // All users (including super_admin) can only see transactions from their company
    const transactionsQuery = query(
      collection(db, 'transactions'),
      where('companyId', '==', targetCompanyId),
      orderBy('date', 'desc')
    );

    const querySnapshot = await getDocs(transactionsQuery);

    return querySnapshot.docs.map(doc => ({
      ...convertTimestamps<Transaction>(doc)
    }));
  } catch (error) {
    // 'Error fetching transactions:', error);
    // Return empty array instead of throwing to handle gracefully in UI
    logApiError('getTransactions', error);
    return [];
  }
}

export async function createTransaction(transaction: Omit<Transaction, 'id' | 'createdAt'>) {
  const docRef = await addDoc(collection(db, 'transactions'), {
    ...transaction,
    createdAt: serverTimestamp()
  });
  return docRef.id;
}

export async function updateTransaction(id: string, transaction: Partial<Transaction>) {
  const docRef = doc(db, 'transactions', id);
  await updateDoc(docRef, {
    ...transaction,
    updatedAt: serverTimestamp()
  });
}

// Dashboard Metrics
export async function getDashboardMetrics() {
  const [invoices, transactions, customers] = await Promise.all([
    getInvoices(),
    getTransactions(),
    getCustomers()
  ]);

  const currentDate = new Date();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

  const metrics = {
    totalOutstanding: 0,
    paidThisMonth: 0,
    overdueAmount: 0,
    totalInvoices: invoices.length,
    totalCustomers: customers.length,
    recentActivity: [] as any[],
    monthlyRevenue: [] as any[]
  };

  // Calculate metrics from invoices
  invoices.forEach(invoice => {
    const dueDate = invoice.dueDate ? new Date(invoice.dueDate) : null;
    const isPastDue = dueDate && dueDate < currentDate;
    
    // Calculate outstanding invoices (sent or overdue)
    if (invoice.status === 'sent' || invoice.status === 'overdue') {
      metrics.totalOutstanding += invoice.total;
    }
    
    // Calculate past due amounts (explicitly marked overdue or past due)
    if (invoice.status === 'overdue' || (invoice.status === 'sent' && isPastDue)) {
      metrics.overdueAmount += invoice.total;
    }
  });

  // Calculate paid this month from transactions
  transactions.forEach(transaction => {
    if (transaction.status === 'paid' && new Date(transaction.date) >= firstDayOfMonth) {
      metrics.paidThisMonth += transaction.amount;
    }
  });

  // Get recent activity
  metrics.recentActivity = [...transactions]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 5);

  // Calculate monthly revenue
  const monthlyData = new Map();
  transactions
    .filter(t => t.status === 'paid')
    .forEach(transaction => {
      const date = new Date(transaction.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleString('default', { month: 'short' });
      
      if (!monthlyData.has(monthKey)) {
        monthlyData.set(monthKey, { month: monthName, amount: 0 });
      }
      monthlyData.get(monthKey).amount += transaction.amount;
    });

  metrics.monthlyRevenue = Array.from(monthlyData.entries())
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([_, value]) => value)
    .slice(-6);

  return metrics;
}

// Enhanced Dashboard Metrics for Business Intelligence
export async function getEnhancedDashboardMetrics(timeframe = 'month', companyId = '') {
  try {
    const metrics = getEmptyDashboardMetrics();
    
    // Get date range
    const { startDate, endDate } = getDateRangesByTimeframe(timeframe, new Date());

    // 'Fetching data for company:', companyId);
    
    // First, fetch necessary data for calculations
    const invoicesRef = collection(db, 'invoices');
    const invoicesQuery = query(
      invoicesRef,
      where('companyId', '==', companyId)
    );
    
    const transactionsRef = collection(db, 'transactions');
    const transactionsQuery = query(
      transactionsRef,
      where('companyId', '==', companyId)
    );

    // Fetch products data as well
    const productsRef = collection(db, 'products');
    const productsQuery = query(
      productsRef,
      where('companyId', '==', companyId)
    );

    // Fetch donations for customer spending analysis
    const donationsRef = collection(db, 'donations');
    const donationsQuery = query(
      donationsRef,
      where('companyId', '==', companyId)
    );

    // Fetch data in parallel
    const [invoiceDocs, transactionDocs, productDocs, donationDocs] = await Promise.all([
      getDocs(invoicesQuery),
      getDocs(transactionsQuery),
      getDocs(productsQuery),
      getDocs(donationsQuery)
    ]);
    
    // 'Total invoices found:', invoiceDocs.size);
    // 'Total transactions found:', transactionDocs.size);
    // 'Total products found:', productDocs.size); // Log product count
    
    // Process invoices
    const invoices = invoiceDocs.docs.map(doc => 
      convertTimestamps<Invoice>(doc) // Proper type conversion
    );
    
    // Process transactions
    const transactions = transactionDocs.docs.map(doc => {
      const data = doc.data();
      // 'Raw transaction data:', {
      
      // Handle undefined dates by using document ID creation time
      let transactionDate;
      if (data.date) {
        transactionDate = data.date instanceof Timestamp ? data.date.toDate() : new Date(data.date);
      } else {
        // If date is undefined, use current date to ensure transaction is counted
        // `Transaction ${doc.id} has no date, using current date`);
        transactionDate = new Date();
      }
      
      return {
        ...data,
        id: doc.id,
        date: transactionDate
      } as unknown as FirebaseTransaction;
    });

    // Process donations
    const donations = donationDocs.docs.map(doc => {
      const data = doc.data();
      return { ...data, id: doc.id };
    });

    // Process products
    const products = productDocs.docs.map(doc => convertTimestamps<Product>(doc));
    const productsMap = new Map(products.map(p => [p.id, p]));
    
    // 'Products Debug:', {
    
    metrics.totalInvoices = invoices.length;

    // Calculate product metrics
    metrics.totalProducts = products.length;
    // Consider products active if they don't have a status or if status is not 'archived'
    // Include subscription products in counts
    metrics.activeProducts = products.filter(p => 
      !p.status || p.status === 'active' || p.status === 'draft'
    ).length;
    metrics.lowStockProducts = products.filter(p => 
      !p.isSubscription && // Only physical products have stock
      (!p.status || p.status === 'active' || p.status === 'draft') && 
      ((p.stockQuantity ?? 0) <= (p.lowStockAlert || 5))
    ); // Remove .length to return array instead of number
    
    // 'Product Metrics Overview:', {

    // Calculate inventory health distribution
    const inventoryHealth = {
      good: 0,
      warning: 0,
      critical: 0
    };

    products.forEach(product => {
      // Skip subscription products for inventory (they don't have physical stock)
      // Include all other products regardless of status
      if (product.isSubscription) return;
      
      const stockLevel = product.stockQuantity ?? 0;
      const alertLevel = product.lowStockAlert || 5;
      
      if (stockLevel === 0) {
        inventoryHealth.critical++;
      } else if (stockLevel <= alertLevel) {
        inventoryHealth.warning++;
      } else {
        inventoryHealth.good++;
      }
    });

    metrics.inventoryHealthDistribution = [
      { status: 'good', value: inventoryHealth.good },
      { status: 'warning', value: inventoryHealth.warning },
      { status: 'critical', value: inventoryHealth.critical }
    ];
    
    // 'Inventory Health Debug:', {

    // Fetch categories to map category IDs to names
    const categoriesRef = collection(db, 'categories');
    const categoriesQuery = query(
      categoriesRef,
      where('companyId', '==', companyId)
    );
    const categoryDocs = await getDocs(categoriesQuery);
    const categoriesMap = new Map(categoryDocs.docs.map(doc => [doc.id, doc.data().name || 'Uncategorized']));

    // Filter transactions by date range and paid status for revenue calculation
    const paidTransactions = transactions.filter(transaction => {
      // Convert transaction date to Date object, handling various formats
      let transactionDate: Date;
      try {
        transactionDate = new Date(transaction.date);
      } catch (_error) {
        // `Invalid date for transaction ${transaction.id}, using current date`);
        transactionDate = new Date();
      }
      
      const isPaid = transaction.status === 'paid';
      const isInDateRange = transactionDate >= startDate && transactionDate <= endDate;
      
      // 'Transaction filter check:', {
      
      return isPaid && isInDateRange;
    });

    // 'Paid transactions in range:', paidTransactions.length);

    // Reset totals
    metrics.totalRevenue = 0;
    metrics.totalCreditCardPayments = 0;
    metrics.totalACHPayments = 0;
    metrics.totalPlatformFees = 0;
    metrics.totalSalesTax = 0;
    metrics.outstandingAmount = 0;
    metrics.outstandingInvoices = 0;
    metrics.overdueAmount = 0;
    metrics.overdueInvoices = 0;
    metrics.aging30Days = 0;
    metrics.aging60Days = 0;
    metrics.aging90Days = 0;
    metrics.aging90PlusDays = 0;
    metrics.newCustomers = 0;
    metrics.averagePurchase = 0;
    metrics.averagePurchaseGrowth = 0;
    metrics.retentionRate = 0;
    metrics.repeatCustomersCount = 0;
    metrics.lifetimeValue = 0;
    metrics.lifetimeValueGrowth = 0;
    metrics.averageMargin = 0;
    metrics.topSellingProduct = null;
    metrics.mostProfitableProduct = null;
    metrics.averagePaymentTime = 0;
    metrics.paymentTimeChange = 0;
    metrics.paymentSuccessRate = 0;
    metrics.totalTransactions = 0;
    metrics.processingFeeRatio = 0.029;
    metrics.processingFeeRatioChange = 0;
    metrics.recentActivity = [];
    metrics.revenueByMonth = [];
    metrics.invoiceStatusDistribution = [];
    metrics.cashFlowForecast = [];
    metrics.topCustomers = [];
    metrics.customerTypeDistribution = [];
    metrics.customerActivity = [];
    metrics.customerPaymentBehavior = [];
    metrics.revenueByCategory = [] as { category: string; revenue: number }[];
    metrics.profitMargins = [] as { name: string; margin: number; profit: number }[];
    metrics.overallMargin = 0;
    metrics.totalProfit = 0;
    metrics.topProductsByRevenue = [];
    metrics.inventoryHealthDistribution = [];
    metrics.productProfitability = [];
    metrics.agedReceivables = [];
    metrics.totalAgedReceivables = {
      current: 0,
      days30: 0,
      days60: 0,
      days90: 0,
      days90plus: 0,
      total: 0
    };
    metrics.invoiceProcessingTime = [];
    metrics.paymentProcessing = [];
    metrics.paymentMethodDistribution = {
      creditCard: 0,
      ach: 0
    };
    metrics.recurringInvoicesCount = 0;
    metrics.oneTimeInvoicesCount = 0;
    metrics.cashFlowTotal = 0;
    metrics.paymentFailureReasons = [] as any[];
    metrics.paymentFailureInsights = [] as any[];
    metrics.paymentRecommendations = [] as any[];
    metrics.alerts = [] as any[];
    metrics.totalSalesTax = 0;
    metrics.totalPlatformFees = 0;
    metrics.totalCreditCardPayments = 0;
    metrics.totalACHPayments = 0;
    metrics.paymentBehaviorTrend = [];
    metrics.customerPaymentTrend = {};
    
    let creditCardCount = 0;
    let achCount = 0;

    // Process all transactions for revenue trends and recent activity
    const currentDate = new Date();
    const transactionsByMonth = new Map();
    const monthsAgo = 12;

    // Chart data needs the full 12-month range regardless of global timeframe
    const chartStartDate = new Date();
    chartStartDate.setMonth(chartStartDate.getMonth() - monthsAgo);
    chartStartDate.setDate(1);
    chartStartDate.setHours(0, 0, 0, 0);

    const chartPaidTransactions = transactions.filter(transaction => {
      let transactionDate: Date;
      try {
        transactionDate = new Date(transaction.date);
      } catch (_error) {
        transactionDate = new Date();
      }
      return transaction.status === 'paid' && transactionDate >= chartStartDate && transactionDate <= endDate;
    });
    
    // Initialize monthly data
    for (let i = 0; i < monthsAgo; i++) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleString('default', { month: 'short', year: '2-digit' });

      transactionsByMonth.set(monthKey, {
        month: monthName,
        sortKey: monthKey,
        revenue: 0,
        cost: 0,
        transactionCount: 0,
        averageValue: 0
      });
    }
    
    // Process paid transactions for revenue metrics (filtered by global timeframe)
    paidTransactions.forEach(transaction => {
      const amount = transaction.amount || 0;
      const paymentMethod = (transaction.paymentMethod || '').toLowerCase();
      const salesTax = Number(transaction.salesTax) || 0;

      metrics.totalRevenue += amount;
      if (salesTax > 0) {
        metrics.totalSalesTax += salesTax;
      }

      if (paymentMethod === 'credit_card') {
        metrics.totalCreditCardPayments += amount;
        creditCardCount++;
      } else if (paymentMethod === 'ach' || paymentMethod === 'bank_transfer') {
        metrics.totalACHPayments += amount;
        achCount++;
      }

      if (transaction.platformFeeAmount) {
        metrics.totalPlatformFees += transaction.platformFeeAmount;
      }
    });

    // Populate monthly chart data using the full 12-month range
    chartPaidTransactions.forEach(transaction => {
      const amount = transaction.amount || 0;
      try {
        const transactionDate = new Date(transaction.date);
        const monthKey = `${transactionDate.getFullYear()}-${String(transactionDate.getMonth() + 1).padStart(2, '0')}`;

        if (transactionsByMonth.has(monthKey)) {
          const monthData = transactionsByMonth.get(monthKey);
          monthData.revenue += amount;
          monthData.transactionCount += 1;
        }
      } catch (_error) {
        // Error processing transaction for monthly chart
      }
    });

    // Initialize revenue and profit calculation variables
    let totalCostOfGoodsSold = 0;
    const revenueByCategoryMap = new Map<string, number>();
    const costByCategoryMap = new Map<string, number>();

    // Process paid invoices for category revenue and profit margins
    const paidInvoices = invoices.filter(inv => inv.status.toLowerCase() === 'paid');

    // 'Processing paid invoices:', {

    paidInvoices.forEach(invoice => {
      // Ensure invoice date is within the timeframe
      const invoiceDate = invoice.createdAt instanceof Date ? invoice.createdAt : new Date(invoice.createdAt || 0);
      if (invoiceDate < startDate || invoiceDate > endDate) {
        return; // Skip invoices outside the timeframe
      }

      // Handle invoices with and without lineItems/items (both field names are used)
      const lineItems = (invoice as any)?.lineItems || (invoice as any)?.items || [];
      
      // Log first few invoices with line items for debugging
      if (lineItems.length > 0 && revenueByCategoryMap.size < 3) {
        // 'Sample invoice with line items:', {
      }
      
      if (lineItems.length > 0) {
        // Process invoices with line items
        lineItems.forEach((item: any) => {
          // First try to use the categoryName from the line item itself
          let categoryName = item.categoryName || 'Uncategorized';
          
          // If no categoryName on item, try to get it from product's category
          if (!item.categoryName && item.productId) {
            const product = productsMap.get(item.productId);
            if (product && product.category) {
              categoryName = categoriesMap.get(product.category) || 'Uncategorized';
            }
          }
          
          // If we have a categoryId but no categoryName, try to map it
          if (!item.categoryName && item.categoryId) {
            categoryName = categoriesMap.get(item.categoryId) || 'Uncategorized';
          }
          
          // Log category assignment for first few items
          if (revenueByCategoryMap.size < 5) {
            // 'Category assignment:', {
          }
          
          const itemRevenue = (item.quantity || 0) * (item.unitPrice || 0);
          
          // Calculate item cost
          let itemCost = 0;
          if (item.productId) {
            const product = productsMap.get(item.productId);
            if (product && product.cost) {
              itemCost = (item.quantity || 0) * (product.cost || 0);
            }
          }

          // Aggregate revenue by category
          revenueByCategoryMap.set(categoryName, (revenueByCategoryMap.get(categoryName) || 0) + itemRevenue);

          // Aggregate cost by category
          costByCategoryMap.set(categoryName, (costByCategoryMap.get(categoryName) || 0) + itemCost);
          totalCostOfGoodsSold += itemCost;
        });
      } else {
        // For invoices without line items, categorize the entire invoice amount as "Uncategorized"
        const categoryName = 'Uncategorized';
        const invoiceRevenue = invoice.total || 0;
        
        // Aggregate revenue by category
        revenueByCategoryMap.set(categoryName, (revenueByCategoryMap.get(categoryName) || 0) + invoiceRevenue);
        
        // No cost information available without line items
        costByCategoryMap.set(categoryName, (costByCategoryMap.get(categoryName) || 0) + 0);
      }
    });

    // Calculate total profit and overall margin
    metrics.totalProfit = metrics.totalRevenue - totalCostOfGoodsSold;
    metrics.overallMargin = metrics.totalRevenue > 0 ? metrics.totalProfit / metrics.totalRevenue : 0; // Return decimal (0-1) instead of percentage

    // Format revenue by category - show top 5 categories plus "Others" if needed
    const sortedCategories = Array.from(revenueByCategoryMap.entries())
      .filter(([_, revenue]) => revenue > 0) // Only include categories with revenue
      .map(([name, revenue]) => ({
        category: name,
        revenue
      }))
      .sort((a, b) => b.revenue - a.revenue);
    
    // If more than 5 categories, group the rest as "Others"
    if (sortedCategories.length > 5) {
      const top5 = sortedCategories.slice(0, 5);
      const othersRevenue = sortedCategories.slice(5).reduce((sum, cat) => sum + cat.revenue, 0);
      
      metrics.revenueByCategory = [
        ...top5,
        { category: 'Others', revenue: othersRevenue }
      ];
    } else if (sortedCategories.length === 0) {
      // No categories found, return empty array
      metrics.revenueByCategory = [];
    } else if (sortedCategories.length === 1 && sortedCategories[0].category === 'Uncategorized' && metrics.totalRevenue > 0) {
      // All revenue is uncategorized - provide a helpful message in console
      // 'All revenue is uncategorized. To see category breakdown:');
      // '1. Ensure products have categories assigned');
      // '2. Ensure invoices have line items with category information');
      // '3. Check that category IDs match between products and categories collection');
      metrics.revenueByCategory = sortedCategories;
    } else {
      metrics.revenueByCategory = sortedCategories;
    }

    // 'Revenue by category data:', {

    // Calculate profit margins by category
    metrics.profitMargins = Array.from(revenueByCategoryMap.keys()).map(categoryName => {
      const revenue = revenueByCategoryMap.get(categoryName) || 0;
      const cost = costByCategoryMap.get(categoryName) || 0;
      const profit = revenue - cost;
      const margin = revenue > 0 ? profit / revenue : 0; // Return decimal (0-1) instead of percentage
      return { name: categoryName, profit, margin };
    }).sort((a, b) => b.profit - a.profit); // Sort by profit descending

    // Calculate product-specific metrics
    const productMetrics = new Map();

    // Initialize product metrics with all products (including subscriptions)
    // Also create a name-to-metrics map for fallback matching
    const productMetricsByName = new Map();
    
    products.forEach(product => {
      // Include all products for metrics tracking
      const metrics = {
        id: product.id,
        name: product.name,
        revenue: 0,
        unitsSold: 0,
        cost: product.cost || 0,
        profit: 0,
        margin: 0,
        category: product.category,
        stockQuantity: product.stockQuantity || 0,
        lowStockAlert: product.lowStockAlert || 5,
        isSubscription: product.isSubscription || false
      };
      
      productMetrics.set(product.id, metrics);
      // Also map by name for fallback matching
      if (product.name) {
        productMetricsByName.set(product.name.toLowerCase().trim(), metrics);
      }
    });

    // Calculate revenue and units sold from paid invoices within timeframe
    paidInvoices.forEach(invoice => {
      // Check if invoice is within timeframe
      const invoiceDate = new Date(invoice.createdAt);
      if (invoiceDate < startDate || invoiceDate > endDate) {
        // `Invoice ${invoice.id} outside timeframe:`, invoiceDate);
        return;
      }
      
      const lineItems = (invoice as any)?.lineItems || (invoice as any)?.items || [];

      lineItems.forEach((item: LineItem) => {
        // Try to find the product metrics by ID first, then by name
        let metrics = null;
        
        if (item.productId && productMetrics.has(item.productId)) {
          // Found by ID
          metrics = productMetrics.get(item.productId);
        } else if (item.productName) {
          // Try to match by name (case insensitive)
          const normalizedName = item.productName.toLowerCase().trim();
          metrics = productMetricsByName.get(normalizedName);
          
          if (metrics) {
            // `Matched product by name: ${item.productName} -> ${metrics.id}`);
          } else {
            // Also try partial matching for common variations
            for (const [name, productMetric] of productMetricsByName.entries()) {
              if (name.includes(normalizedName) || normalizedName.includes(name)) {
                metrics = productMetric;
                // `Matched product by partial name: ${item.productName} -> ${productMetric.name}`);
                break;
              }
            }
          }
        }
        
        if (!metrics) {
          return;
        }
        
        const quantity = Number(item.quantity) || 0;
        const unitPrice = Number(item.unitPrice) || 0;
        const itemRevenue = quantity * unitPrice;
        const itemCost = quantity * (metrics.cost || 0);
        const itemProfit = itemRevenue - itemCost;
        
        metrics.revenue += itemRevenue;
        metrics.unitsSold += quantity;
        metrics.profit += itemProfit;
        // Calculate margin as a percentage
        metrics.margin = metrics.revenue > 0 ? metrics.profit / metrics.revenue : 0; // Return decimal (0-1) instead of percentage
      });
    });
    
    // 'Product Revenue Calculation Summary:', {

    // Convert to array - include ALL products for display
    const allProductMetrics = Array.from(productMetrics.values());
    
    // 'Product Metrics Debug:', {
    
    // Create filtered array only for "top" products (those with sales)
    const productMetricsWithSales = allProductMetrics
      .filter(p => p.revenue > 0 || p.unitsSold > 0)
      .sort((a, b) => b.revenue - a.revenue);

    // Set top products by revenue - show top 5 products sorted by revenue
    // This ensures the chart displays even if products have no sales yet
    metrics.topProductsByRevenue = allProductMetrics
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5) // Show only top 5 as requested
      .map(p => ({
        id: p.id,
        name: p.name,
        revenue: p.revenue,
        unitsSold: p.unitsSold
      }));
    
    // Remove sample data fallback - show empty state instead
    // if (metrics.topProductsByRevenue.length === 0) {
    //   // "No products with revenue found, adding sample data for debugging");
    //   metrics.topProductsByRevenue = [
    //     { id: 'sample1', name: 'Sample Product 1', revenue: 5000, unitsSold: 10 },
    //     { id: 'sample2', name: 'Sample Product 2', revenue: 4000, unitsSold: 8 },
    //     { id: 'sample3', name: 'Sample Product 3', revenue: 3000, unitsSold: 6 },
    //     { id: 'sample4', name: 'Sample Product 4', revenue: 2000, unitsSold: 4 },
    //     { id: 'sample5', name: 'Sample Product 5', revenue: 1000, unitsSold: 2 }
    //   ];
    // }

    // Find top selling product by units sold (from products with sales)
    const topSelling = productMetricsWithSales
      .sort((a, b) => b.unitsSold - a.unitsSold)[0];

    if (topSelling) {
      metrics.topSellingProduct = {
        id: topSelling.id,
        name: topSelling.name,
        revenue: topSelling.revenue,
        unitsSold: topSelling.unitsSold
      };
    }

    // Find most profitable product by absolute profit amount (from products with sales)
    const mostProfitable = productMetricsWithSales
      .sort((a, b) => b.profit - a.profit)[0];

    if (mostProfitable) {
      metrics.mostProfitableProduct = {
        id: mostProfitable.id,
        name: mostProfitable.name,
        profit: mostProfitable.profit,
        margin: mostProfitable.margin
      };
    }

    // Calculate overall average margin (from all products, not just those with sales)
    const productTotalRevenue = allProductMetrics.reduce((sum, p) => sum + p.revenue, 0);
    const totalProfit = allProductMetrics.reduce((sum, p) => sum + p.profit, 0);
    metrics.averageMargin = productTotalRevenue > 0 ? totalProfit / productTotalRevenue : 0; // Return decimal (0-1) instead of percentage

    // Update product profitability table - show ALL products
    metrics.productProfitability = allProductMetrics
      .map(p => ({
        id: p.id,
        name: p.name,
        category: categoriesMap.get(p.category) || 'Uncategorized',
        unitsSold: p.unitsSold,
        revenue: p.revenue,
        cost: p.unitsSold * (p.cost || 0),
        profit: p.profit,
        margin: p.margin
      }))
      .sort((a, b) => b.profit - a.profit);
      
    // Remove sample profitability data - show empty state instead
    // if (metrics.productProfitability.length === 0) {
    //   // "No products with profitability data found, adding sample data for debugging");
    //   metrics.productProfitability = [
    //     { id: 'sample1', name: 'Sample Product 1', category: 'Sample Category', unitsSold: 10, revenue: 5000, cost: 2500, profit: 2500, margin: 50 },
    //     { id: 'sample2', name: 'Sample Product 2', category: 'Sample Category', unitsSold: 8, revenue: 4000, cost: 2400, profit: 1600, margin: 40 },
    //     { id: 'sample3', name: 'Sample Product 3', category: 'Sample Category', unitsSold: 6, revenue: 3000, cost: 2100, profit: 900, margin: 30 },
    //     { id: 'sample4', name: 'Sample Product 4', category: 'Sample Category', unitsSold: 4, revenue: 2000, cost: 1600, profit: 400, margin: 20 },
    //     { id: 'sample5', name: 'Sample Product 5', category: 'Sample Category', unitsSold: 2, revenue: 1000, cost: 900, profit: 100, margin: 10 }
    //   ];
    // }
    
    // Remove sample data fallback - if no data exists, leave empty for proper empty state handling
    // if (!metrics.topSellingProduct) {
    //   metrics.topSellingProduct = {
    //     id: 'sample1',
    //     name: 'Sample Product 1',
    //     revenue: 5000,
    //     unitsSold: 10
    //   };
    // }
    
    // if (!metrics.mostProfitableProduct) {
    //   metrics.mostProfitableProduct = {
    //     id: 'sample1',
    //     name: 'Sample Product 1',
    //     profit: 2500,
    //     margin: 50
    //   };
    // }

    // Calculate Operations Metrics - Add missing calculations
    // 1. Average Payment Time
    const paymentTimes: number[] = [];
    paidInvoices.forEach(invoice => {
      if (invoice.createdAt && invoice.paymentTransactionId) {
        // Find the payment transaction for this invoice
        const paymentTransaction = transactions.find(t => t.id === invoice.paymentTransactionId);
        if (paymentTransaction && paymentTransaction.date) {
          const createdDate = invoice.createdAt instanceof Date ? invoice.createdAt : new Date(invoice.createdAt);
          const paidDate = new Date(paymentTransaction.date);
          const daysToPay = Math.floor((paidDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
          if (daysToPay >= 0) {
            paymentTimes.push(daysToPay);
          }
        }
      }
    });
    
    metrics.averagePaymentTime = paymentTimes.length > 0 
      ? Math.round(paymentTimes.reduce((sum, days) => sum + days, 0) / paymentTimes.length) 
      : 0;

    // Calculate previous period payment time for trend comparison
    const previousPeriodPaymentTimes: number[] = [];
    const previousStartDate = new Date(startDate);
    const previousEndDate = new Date(endDate);
    const periodLength = endDate.getTime() - startDate.getTime();
    previousStartDate.setTime(startDate.getTime() - periodLength);
    previousEndDate.setTime(endDate.getTime() - periodLength);

    paidInvoices.forEach(invoice => {
      if (invoice.createdAt && invoice.paymentTransactionId) {
        const paymentTransaction = transactions.find(t => t.id === invoice.paymentTransactionId);
        if (paymentTransaction && paymentTransaction.date) {
          const createdDate = invoice.createdAt instanceof Date ? invoice.createdAt : new Date(invoice.createdAt);
          const paidDate = new Date(paymentTransaction.date);
          
          // Check if this payment falls in the previous period
          if (createdDate >= previousStartDate && createdDate <= previousEndDate) {
            const daysToPay = Math.floor((paidDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
            if (daysToPay >= 0) {
              previousPeriodPaymentTimes.push(daysToPay);
            }
          }
        }
      }
    });
    
    const previousAveragePaymentTime = previousPeriodPaymentTimes.length > 0 
      ? Math.round(previousPeriodPaymentTimes.reduce((sum, days) => sum + days, 0) / previousPeriodPaymentTimes.length) 
      : 0;
    
    // Calculate change (negative is good for payment time)
    metrics.paymentTimeChange = previousAveragePaymentTime > 0 
      ? ((metrics.averagePaymentTime - previousAveragePaymentTime) / previousAveragePaymentTime) * 100 
      : 0;

    // 2. Payment Success Rate
    const totalPaymentAttempts = transactions.length;
    const successfulPayments = transactions.filter(t => t.status === 'paid').length;
    metrics.paymentSuccessRate = totalPaymentAttempts > 0 ? successfulPayments / totalPaymentAttempts : 0;

    // 3. Processing Fee Ratio
    metrics.processingFeeRatio = metrics.totalRevenue > 0 
      ? (metrics.totalPlatformFees || 0) / metrics.totalRevenue 
      : 0;
    
    // Calculate previous period processing fee ratio
    const previousTransactions = transactions.filter(transaction => {
      const transactionDate = new Date(transaction.date);
      return transactionDate >= previousStartDate && transactionDate <= previousEndDate && transaction.status === 'paid';
    });
    
    const previousRevenue = previousTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);
    const previousFees = previousTransactions.reduce((sum, t) => sum + (t.platformFeeAmount || 0), 0);
    const previousProcessingFeeRatio = previousRevenue > 0 ? previousFees / previousRevenue : 0;
    
    metrics.processingFeeRatioChange = previousProcessingFeeRatio > 0
      ? ((metrics.processingFeeRatio - previousProcessingFeeRatio) / previousProcessingFeeRatio) * 100
      : 0;

    // Revenue growth: compare current period to previous period
    metrics.revenueGrowth = previousRevenue > 0
      ? ((metrics.totalRevenue - previousRevenue) / previousRevenue) * 100
      : 0;

    // 4. Invoice Processing Time by Month
    const processingTimeByMonth = new Map();
    // Initialize months
    for (let i = 0; i < 6; i++) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleString('default', { month: 'short' });
      
      processingTimeByMonth.set(monthKey, {
        month: monthName,
        days: 0,
        count: 0
      });
    }

    // Calculate processing times by month using transaction data
    paidInvoices.forEach(invoice => {
      if (invoice.createdAt && invoice.paymentTransactionId) {
        const paymentTransaction = transactions.find(t => t.id === invoice.paymentTransactionId);
        if (paymentTransaction && paymentTransaction.date) {
          const createdDate = invoice.createdAt instanceof Date ? invoice.createdAt : new Date(invoice.createdAt);
          const paidDate = new Date(paymentTransaction.date);
          const monthKey = `${createdDate.getFullYear()}-${String(createdDate.getMonth() + 1).padStart(2, '0')}`;
          
          if (processingTimeByMonth.has(monthKey)) {
            const daysToPay = Math.floor((paidDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
            if (daysToPay >= 0) {
              const monthData = processingTimeByMonth.get(monthKey);
              monthData.days += daysToPay;
              monthData.count += 1;
            }
          }
        }
      }
    });

    // Calculate average processing time per month
    metrics.invoiceProcessingTime = Array.from(processingTimeByMonth.values())
      .map(monthData => ({
        month: monthData.month,
        days: monthData.count > 0 ? Math.round(monthData.days / monthData.count) : 0
      }))
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-6);

    // 5. Payment Processing Success/Failure by Month
    const paymentProcessingByMonth = new Map();
    // Initialize months
    for (let i = 0; i < 6; i++) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleString('default', { month: 'short' });
      
      paymentProcessingByMonth.set(monthKey, {
        month: monthName,
        success: 0,
        failed: 0
      });
    }

    // Count transactions by month and status
    transactions.forEach(transaction => {
      const transactionDate = new Date(transaction.date);
      const monthKey = `${transactionDate.getFullYear()}-${String(transactionDate.getMonth() + 1).padStart(2, '0')}`;
      
      if (paymentProcessingByMonth.has(monthKey)) {
        const monthData = paymentProcessingByMonth.get(monthKey);
        if (transaction.status === 'paid') {
          monthData.success += 1;
        } else if (transaction.status === 'failed') {
          monthData.failed += 1;
        }
      }
    });

    metrics.paymentProcessing = Array.from(paymentProcessingByMonth.values())
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-6);

    // 6. Payment Failure Reasons Analysis
    const failureReasons = new Map();
    const failedTransactions = transactions.filter(t => t.status === 'failed');
    
    failedTransactions.forEach(transaction => {
      const reason = transaction.failureReason || 'Unknown';
      failureReasons.set(reason, (failureReasons.get(reason) || 0) + 1);
    });

    metrics.paymentFailureReasons = Array.from(failureReasons.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    // 7. Generate Actionable Insights and Recommendations
    metrics.paymentFailureInsights = [];
    metrics.paymentRecommendations = [];

    if (metrics.paymentSuccessRate < 0.9) {
      metrics.paymentFailureInsights.push({
        type: 'critical',
        title: 'Low Payment Success Rate',
        description: `Success rate is ${Math.round(metrics.paymentSuccessRate * 100)}%. Consider reviewing payment processing setup.`
      });
    }

    if (metrics.averagePaymentTime > 15) {
      metrics.paymentFailureInsights.push({
        type: 'warning',
        title: 'Slow Payment Processing',
        description: `Average payment time is ${metrics.averagePaymentTime} days. Consider offering early payment discounts.`
      });
    }

    if (metrics.processingFeeRatio > 0.03) {
      metrics.paymentRecommendations.push({
        title: 'Optimize Payment Processing Fees',
        description: 'Processing fees are above 3%. Consider negotiating better rates or encouraging ACH payments.'
      });
    }

    if (metrics.paymentMethodDistribution?.ach && metrics.paymentMethodDistribution.ach < 0.3) {
      metrics.paymentRecommendations.push({
        title: 'Promote ACH Payments',
        description: 'ACH payments have lower processing fees. Consider offering incentives for ACH usage.'
      });
    }

    // Add total transactions count for display
    metrics.totalTransactions = transactions.length;

    // Count invoices by status with proper detection of past due
    const invoicesByStatus = {
      draft: 0,
      sent: 0,
      sent_past_due: 0,  // Tracking sent invoices that are past due
      paid: 0,
      overdue: 0,
      cancelled: 0,
      refunded: 0
    };
    
    // Create a map to track aging by customer
    const customerAgingMap = new Map<string, {
      customerId: string;
      customerName: string;
      customerCompanyName?: string;
      current: number;
      days30: number;
      days60: number;
      days90: number;
      days90plus: number;
      total: number;
    }>();

    // Process invoices for outstanding and overdue amounts
    invoices.forEach((invoice: Invoice) => {  // Explicit Invoice type
      const dueDate = invoice.dueDate ? new Date(invoice.dueDate) : null;
      const isPastDue = dueDate && dueDate < currentDate;

      switch(invoice.status.toLowerCase()) {  // Handle case variations
        case 'draft':
          invoicesByStatus.draft++;
          break;
        case 'paid':
          invoicesByStatus.paid++;
          break;
        case 'cancelled':
          invoicesByStatus.cancelled++;
          break;
        case 'overdue':
          invoicesByStatus.overdue++;
          break;
        case 'refunded':
          invoicesByStatus.refunded++;
          break;
        case 'sent':
          if (isPastDue) {
            invoicesByStatus.sent_past_due++;
          } else {
            invoicesByStatus.sent++;
          }
          break;
      }
      
      // Calculate outstanding invoices (sent or overdue)
      if (invoice.status.toLowerCase() === 'sent' || invoice.status.toLowerCase() === 'overdue') {
        metrics.outstandingAmount += invoice.total || 0;
        metrics.outstandingInvoices++;
        metrics.totalOutstanding += invoice.total || 0;

        // Track customer aging for outstanding invoices
        if (invoice.customerId) {
          if (!customerAgingMap.has(invoice.customerId)) {
            customerAgingMap.set(invoice.customerId, {
              customerId: invoice.customerId,
              customerName: invoice.customerName || 'Unknown Customer',
              customerCompanyName: invoice.customerCompanyName,
              current: 0,
              days30: 0,
              days60: 0,
              days90: 0,
              days90plus: 0,
              total: 0
            });
          }

          const customerAging = customerAgingMap.get(invoice.customerId)!;
          const invoiceAmount = invoice.total || 0;
          customerAging.total += invoiceAmount;

          // Categorize by age
          if (!isPastDue) {
            // Current (not past due)
            customerAging.current += invoiceAmount;
          } else if (dueDate) {
            const daysPastDue = Math.floor((currentDate.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
            
            if (daysPastDue >= 1 && daysPastDue <= 30) {
              customerAging.days30 += invoiceAmount;
            } else if (daysPastDue >= 31 && daysPastDue <= 60) {
              customerAging.days60 += invoiceAmount;
            } else if (daysPastDue >= 61 && daysPastDue <= 90) {
              customerAging.days90 += invoiceAmount;
            } else if (daysPastDue >= 91) {
              customerAging.days90plus += invoiceAmount;
            }
          }
        }
      }
      
      // Calculate past due amounts (explicitly marked overdue or past due)
      if (invoice.status.toLowerCase() === 'overdue' || (invoice.status.toLowerCase() === 'sent' && isPastDue)) {
        metrics.overdueAmount += invoice.total || 0;
        metrics.overdueInvoices++;
        
        // Add aging calculations with correct aging buckets based on due date
        if (dueDate) {
          const daysPastDue = Math.floor((currentDate.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
          
          // `Aging calculation for invoice ${invoice.id}: ${daysPastDue} days past due, amount: ${invoice.total}`);
          
          // Standard accounts receivable aging buckets (based on due date, not creation date)
          if (daysPastDue >= 1 && daysPastDue <= 30) {
            metrics.aging30Days += invoice.total || 0;
            // `Added ${invoice.total} to aging30Days, new total: ${metrics.aging30Days}`);
          } else if (daysPastDue >= 31 && daysPastDue <= 60) {
            metrics.aging60Days += invoice.total || 0;
            // `Added ${invoice.total} to aging60Days, new total: ${metrics.aging60Days}`);
          } else if (daysPastDue >= 61 && daysPastDue <= 90) {
            metrics.aging90Days += invoice.total || 0;
            // `Added ${invoice.total} to aging90Days, new total: ${metrics.aging90Days}`);
          } else if (daysPastDue >= 91) {
            metrics.aging90PlusDays += invoice.total || 0;
            // `Added ${invoice.total} to aging90PlusDays, new total: ${metrics.aging90PlusDays}`);
          }
        }
      }
    });

    // Convert customer aging map to array for display
    metrics.agedReceivables = Array.from(customerAgingMap.values())
      .filter(customer => customer.total > 0) // Only include customers with outstanding balances
      .map(customer => ({
        name: customer.customerCompanyName 
          ? `${customer.customerCompanyName} (${customer.customerName})`
          : customer.customerName,
        current: customer.current,
        days30: customer.days30,
        days60: customer.days60,
        days90: customer.days90,
        days90plus: customer.days90plus,
        total: customer.total
      }))
      .sort((a, b) => b.total - a.total); // Sort by total amount descending

    // Calculate payment method distribution
    const totalTransactions = creditCardCount + achCount;
    if (totalTransactions > 0) {
      metrics.paymentMethodDistribution = {
        ach: achCount / totalTransactions,
        creditCard: creditCardCount / totalTransactions
      };
    }

    // Create invoice status distribution for pie chart with past due state reflected
    metrics.invoiceStatusDistribution = [
      { status: 'paid', value: invoicesByStatus.paid },
      { status: 'sent', value: invoicesByStatus.sent },
      { status: 'overdue', value: invoicesByStatus.overdue + invoicesByStatus.sent_past_due }, // Combine overdue with sent_past_due
      { status: 'draft', value: invoicesByStatus.draft },
      { status: 'cancelled', value: invoicesByStatus.cancelled },
      { status: 'refunded', value: invoicesByStatus.refunded }
    ];

    // Calculate monthly costs based on invoice line items and product costs
    // 'Calculating monthly costs from paid invoices...', {
    
    paidInvoices.forEach(invoice => {
      // Get invoice date
      const invoiceDate = invoice.createdAt instanceof Date ? invoice.createdAt : new Date(invoice.createdAt || 0);
      const monthKey = `${invoiceDate.getFullYear()}-${String(invoiceDate.getMonth() + 1).padStart(2, '0')}`;
      
      // Only process if this month is in our tracking period
      if (transactionsByMonth.has(monthKey)) {
        const monthData = transactionsByMonth.get(monthKey);
        
        // Get line items from invoice
        const lineItems = (invoice as any)?.lineItems || (invoice as any)?.items || [];
        
        // Calculate cost for each line item
        let invoiceCost = 0;
        lineItems.forEach((item: any) => {
          if (item.productId) {
            const product = productsMap.get(item.productId);
            if (product && product.cost) {
              const itemCost = (item.quantity || 0) * (product.cost || 0);
              invoiceCost += itemCost;
            }
          }
        });
        
        // Add to monthly cost
        monthData.cost += invoiceCost;
      }
    });
    
    // 'Monthly cost calculation complete:', {

    // Convert monthly data to array sorted chronologically (oldest first for chart display)
    const monthlyDataArray = Array.from(transactionsByMonth.values())
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
      .map(monthData => ({
        ...monthData,
        averageValue: monthData.transactionCount > 0 ? monthData.revenue / monthData.transactionCount : 0
      }));

    metrics.revenueByMonth = monthlyDataArray;

    // Get recent activity from all transactions to ensure we show the most recent
    metrics.recentActivity = [...transactions]
      .filter(t => t.status === 'paid' || t.status === 'failed' || t.status === 'refunded')
      .sort((a, b) => {
        // Sort by date descending (most recent first)
        const dateA = new Date(a.date || 0).getTime();
        const dateB = new Date(b.date || 0).getTime();
        return dateB - dateA;
      })
      .slice(0, 5)
      .map(transaction => {
        // Enhance transaction with customer details
        const customer = transaction.customerId ? 
          invoices.find((inv: Invoice) => inv.customerId === transaction.customerId) : null;
          
        return {
          ...transaction,
          customerCompanyName: transaction.customerCompanyName || 
            (customer ? customer.customerCompanyName : null),
          customerName: transaction.customerName || 
            (customer ? customer.customerName : 'Unknown Customer')
        };
      });

    // Process customer data
    const customersQuery = query(
      collection(db, 'customers'),
      where('companyId', '==', companyId)
    );
    
    const customersSnapshot = await getDocs(customersQuery);
    // 'Customer data found:', customersSnapshot.size);
    
    const customers = customersSnapshot.docs.map(doc => convertTimestamps<any>(doc));
    
    // Calculate total customers
    metrics.totalCustomers = customers.length;
    
    // Calculate new customers (created within the timeframe)
    metrics.newCustomers = customers.filter(c => {
      if (!c.createdAt) return false;
      const createdDate = c.createdAt instanceof Date ? c.createdAt : new Date(c.createdAt);
      return createdDate >= startDate && createdDate <= endDate;
    }).length;
    
    // Process customer types distribution
    const customerTypes = new Map();
    customers.forEach(customer => {
      const type = customer.type || 'Unknown';
      if (!customerTypes.has(type)) {
        customerTypes.set(type, { 
          name: type, 
          value: 0,
          revenue: 0 
        });
      }
      customerTypes.get(type).value++;
    });
    
    // Add revenue to customer types
    invoices.forEach(invoice => {
      if (!invoice.customerId) return;
      
      const customer = customers.find(c => c.id === invoice.customerId);
      if (!customer) return;
      
      const type = customer.type || 'Unknown';
      if (customerTypes.has(type)) {
        customerTypes.get(type).revenue += invoice.total || 0;
      }
    });
    
    metrics.customerTypeDistribution = Array.from(customerTypes.values());
    
    // Process top customers (from both invoices and donations)
    const customerSpending = new Map();
    invoices.forEach(invoice => {
      if (!invoice.customerId) return;

      if (!customerSpending.has(invoice.customerId)) {
        customerSpending.set(invoice.customerId, {
          customerId: invoice.customerId,
          invoicesCount: 0,
          totalSpent: 0
        });
      }

      customerSpending.get(invoice.customerId).invoicesCount++;
      if (invoice.status.toLowerCase() === 'paid') {
        customerSpending.get(invoice.customerId).totalSpent += invoice.total || 0;
      }
    });

    // Include donation spending in customer data
    donations.forEach((donation: any) => {
      if (!donation.customerId || donation.status !== 'completed') return;

      if (!customerSpending.has(donation.customerId)) {
        customerSpending.set(donation.customerId, {
          customerId: donation.customerId,
          invoicesCount: 0,
          totalSpent: 0
        });
      }

      customerSpending.get(donation.customerId).invoicesCount++;
      customerSpending.get(donation.customerId).totalSpent += donation.amount || 0;
    });
    
    // Calculate percentage of revenue for each customer (rename totalRevenue to customerTotalRevenue)
    const customerTotalRevenue = metrics.totalRevenue;
    
    // Enhance customer spending data with customer details
    const topCustomers = Array.from(customerSpending.values())
      .map(spending => {
        const customer = customers.find(c => c.id === spending.customerId);
        if (!customer) return null;
        
        return {
          firstName: customer.firstName,
          lastName: customer.lastName,
          companyName: customer.companyName,
          email: customer.email,
          type: customer.type,
          invoicesCount: spending.invoicesCount,
          totalSpent: spending.totalSpent,
          percentageOfRevenue: customerTotalRevenue > 0 ? (spending.totalSpent / customerTotalRevenue) * 100 : 0
        };
      })
      .filter(customer => customer !== null)
      .sort((a, b) => b.totalSpent - a.totalSpent)
      .slice(0, 5);
    
    metrics.topCustomers = topCustomers;
    
    // Calculate customer payment behavior
    const customerPaymentTimes = new Map();
    
    // Process paid invoices to calculate payment behavior
    // Rename this variable
    const paidInvoicesForBehavior = invoices.filter(inv => inv.status.toLowerCase() === 'paid');
    
    // Use the renamed variable here
    paidInvoicesForBehavior.forEach(invoice => {
      if (!invoice.customerId || !invoice.dueDate || !invoice.paymentTransactionId) return;
      
      // Find the payment transaction
      const transaction = transactions.find(t => t.id === invoice.paymentTransactionId);
      if (!transaction || !transaction.date) return;
      
      // Calculate days to pay
      const dueDate = new Date(invoice.dueDate);
      // Ensure paymentDate is a Date object before calling getTime()
      let paymentDate: Date | null = null; // Initialize as null

      // Check the type of transaction.date safely
      const rawDate = transaction.date; // Assign to a variable for type narrowing

      // Revised Type Checking Logic:
      if (rawDate && typeof rawDate === 'object' && typeof (rawDate as any).toDate === 'function') {
        // Likely a Firestore Timestamp
        paymentDate = (rawDate as Timestamp).toDate();
      // Check if it behaves like a Date object (has getTime method) - Replaced instanceof Date
      } else if (rawDate && typeof rawDate === 'object' && typeof (rawDate as any).getTime === 'function') {
        // Standard JavaScript Date object (or something behaving like one)
        // We cast to unknown first, then Date, to satisfy TypeScript
        paymentDate = rawDate as unknown as Date;
      } else if (typeof rawDate === 'string' || typeof rawDate === 'number') {
        // Attempt to parse string or number
        const parsedDate = new Date(rawDate);
        if (!isNaN(parsedDate.getTime())) {
          paymentDate = parsedDate;
        }
      }

      // Handle cases where date might be invalid or missing
      if (!paymentDate) {
        // `Invalid or missing payment date for transaction ${transaction.id}`);
        return; // Skip this invoice if payment date is unusable
      }

      // Now paymentDate should be a valid Date object
      // Ensure getTime() exists before calling, just in case
      if (typeof paymentDate.getTime !== 'function') {
          // `Invalid paymentDate object for transaction ${transaction.id}`, paymentDate);
          return; // Skip if it's not a valid Date-like object
      }
      const daysToPay = Math.max(0, Math.floor((paymentDate.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)));
      
      if (!customerPaymentTimes.has(invoice.customerId)) {
        customerPaymentTimes.set(invoice.customerId, {
          customerId: invoice.customerId,
          paymentTimes: [],
          onTimePayments: 0,
          totalPayments: 0,
          totalSpent: 0,
          preferredPaymentMethod: transaction.paymentMethod || 'credit_card'
        });
      }
      
      const customerData = customerPaymentTimes.get(invoice.customerId);
      customerData.paymentTimes.push(daysToPay);
      customerData.totalPayments++;
      customerData.totalSpent += invoice.total || 0;
      
      // Count on-time payments (paid by or before due date)
      if (daysToPay <= 0) {
        customerData.onTimePayments++;
      }
      
      // Update preferred payment method (most recent transaction's method)
      if (transaction.paymentMethod) {
        customerData.preferredPaymentMethod = transaction.paymentMethod;
      }
    });
    
    // Calculate average payment times and on-time rates
    const customerPaymentBehavior = Array.from(customerPaymentTimes.values())
      .map(data => {
        const customer = customers.find(c => c.id === data.customerId);
        if (!customer) return null;
        
        // Calculate average days to pay
        const avgDaysToPay = data.paymentTimes.length > 0 
          ? Math.round(data.paymentTimes.reduce((sum, days) => sum + days, 0) / data.paymentTimes.length) 
          : 0;
        
        // Calculate on-time payment rate
        const onTimeRate = data.totalPayments > 0 
          ? (data.onTimePayments / data.totalPayments) * 100 
          : 0;
        
        // Determine customer payment status
        let status = 'good';
        if (avgDaysToPay > 30) {
          status = 'poor';
        } else if (avgDaysToPay > 15) {
          status = 'average';
        }
        
        return {
          id: data.customerId,
          name: customer.firstName && customer.lastName 
            ? `${customer.firstName} ${customer.lastName}`
            : 'Unknown',
          companyName: customer.companyName,
          avgDaysToPay,
          onTimeRate,
          totalSpent: data.totalSpent,
          preferredPaymentMethod: data.preferredPaymentMethod,
          status
        };
      })
      .filter(customer => customer !== null)
      .sort((a, b) => b.totalSpent - a.totalSpent)
      .slice(0, 5);
    
    metrics.customerPaymentBehavior = customerPaymentBehavior;
    
    // Calculate payment behavior trend over time (last 30 days)
    const paymentBehaviorTrend = [];
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    // Group paid invoices by day and calculate average payment time
    const dailyPaymentTimes = new Map();
    
    paidInvoices.forEach(invoice => {
      // For paid invoices, use updatedAt as the payment date
      const paidDate = invoice.updatedAt instanceof Date ? invoice.updatedAt : new Date(invoice.updatedAt || 0);
      const createdDate = invoice.createdAt instanceof Date ? invoice.createdAt : new Date(invoice.createdAt || 0);
      
      if (paidDate >= thirtyDaysAgo) {
        const dayKey = format(paidDate, 'yyyy-MM-dd');
        const daysToPay = Math.max(0, Math.floor((paidDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24)));
        
        if (!dailyPaymentTimes.has(dayKey)) {
          dailyPaymentTimes.set(dayKey, {
            date: dayKey,
            times: [],
            avgPaymentTime: 0
          });
        }
        
        dailyPaymentTimes.get(dayKey).times.push(daysToPay);
      }
    });
    
    // Calculate averages and create trend data
    dailyPaymentTimes.forEach((data, _dayKey) => {
      data.avgPaymentTime = Math.round(
        data.times.reduce((sum, time) => sum + time, 0) / data.times.length
      );
      paymentBehaviorTrend.push({
        date: data.date,
        avgPaymentTime: data.avgPaymentTime
      });
    });
    
    // Sort by date
    paymentBehaviorTrend.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    metrics.paymentBehaviorTrend = paymentBehaviorTrend;
    
    // Calculate customer activity (transaction count per customer)
    const customerActivity = new Map();
    
    // Process all transactions to count activities per customer
    transactions.forEach(transaction => {
      if (!transaction.customerId) return;
      
      if (!customerActivity.has(transaction.customerId)) {
        const customer = customers.find(c => c.id === transaction.customerId);
        if (!customer) return;
        
        customerActivity.set(transaction.customerId, {
          id: transaction.customerId,
          name: customer.companyName || 
            (customer.firstName && customer.lastName ? 
              `${customer.firstName} ${customer.lastName}` : 'Unknown'),
          transactions: 0,
          lastTransaction: null
        });
      }
      
      const customerData = customerActivity.get(transaction.customerId);
      customerData.transactions++;
      
      // Update last transaction date if newer
      // Add type assertion here
      const transactionDate = (transaction.date as any) instanceof Date ? 
        transaction.date : new Date(transaction.date || 0);
        
      if (!customerData.lastTransaction || 
          transactionDate > customerData.lastTransaction) {
        customerData.lastTransaction = transactionDate;
      }
    });
    
    // Sort customers by transaction count and get top 10
    metrics.customerActivity = Array.from(customerActivity.values())
      .sort((a, b) => b.transactions - a.transactions)
      .slice(0, 10);
      
    // Calculate customer lifetime value and retention metrics
    
    // For lifetime value calculation
    const customerLifetimeValue = metrics.totalRevenue > 0 && metrics.totalCustomers > 0 
      ? metrics.totalRevenue / metrics.totalCustomers 
      : 0;
    
    metrics.lifetimeValue = customerLifetimeValue;
    
    // Calculate lifetime value growth using previous period comparison
    const previousPeriodCustomers = customers.filter(c => {
      if (!c.createdAt) return false;
      const createdDate = c.createdAt instanceof Date ? c.createdAt : new Date(c.createdAt);
      return createdDate < startDate;
    });
    const previousLifetimeValue = previousRevenue > 0 && previousPeriodCustomers.length > 0
      ? previousRevenue / previousPeriodCustomers.length
      : 0;
    metrics.lifetimeValueGrowth = previousLifetimeValue > 0
      ? ((customerLifetimeValue - previousLifetimeValue) / previousLifetimeValue) * 100
      : 0;

    // Calculate average purchase value (include both invoices and donations)
    const paidInvoiceCount = invoices.filter(inv => inv.status.toLowerCase() === 'paid').length;
    const completedDonationCount = donations.filter((d: any) => d.status === 'completed').length;
    const totalPurchaseCount = paidInvoiceCount + completedDonationCount;
    metrics.averagePurchase = metrics.totalRevenue > 0 && totalPurchaseCount > 0
      ? metrics.totalRevenue / totalPurchaseCount
      : 0;

    // Estimate retention rate based on repeat customers (invoices + donations)
    const customerWithMultiplePurchases = new Set();
    const customerWithAtLeastOnePurchase = new Set();

    invoices.forEach(invoice => {
      if (!invoice.customerId) return;
      if (customerWithAtLeastOnePurchase.has(invoice.customerId)) {
        customerWithMultiplePurchases.add(invoice.customerId);
      } else {
        customerWithAtLeastOnePurchase.add(invoice.customerId);
      }
    });

    donations.forEach((donation: any) => {
      if (!donation.customerId || donation.status !== 'completed') return;
      if (customerWithAtLeastOnePurchase.has(donation.customerId)) {
        customerWithMultiplePurchases.add(donation.customerId);
      } else {
        customerWithAtLeastOnePurchase.add(donation.customerId);
      }
    });

    metrics.repeatCustomersCount = customerWithMultiplePurchases.size;

    metrics.retentionRate = customerWithAtLeastOnePurchase.size > 0
      ? (customerWithMultiplePurchases.size / customerWithAtLeastOnePurchase.size)
      : 0;

    // 'Final calculated metrics:', {

    // Calculate Cash Flow Projection for next 30 days
    const thirtyDaysFromNow = new Date(currentDate);
    thirtyDaysFromNow.setDate(currentDate.getDate() + 30);
    
    // Initialize cash flow forecast data for next 30 days
    const cashFlowByDay = new Map();
    for (let i = 1; i <= 30; i++) {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() + i);
      const dateKey = date.toISOString().split('T')[0];
      cashFlowByDay.set(dateKey, {
        day: i,
        date: dateKey,
        amount: 0,
        invoiceCount: 0,
        type: 'outstanding' // Track if this is from outstanding invoices
      });
    }
    
    // Calculate projected cash flow from outstanding invoices
    const outstandingInvoices = invoices.filter(inv => 
      inv.status.toLowerCase() === 'sent' || inv.status.toLowerCase() === 'overdue'
    );
    
    // `Calculating cash flow from ${outstandingInvoices.length} outstanding invoices`);
    
    outstandingInvoices.forEach(invoice => {
      if (invoice.dueDate) {
        const dueDate = new Date(invoice.dueDate);
        const dueDateKey = dueDate.toISOString().split('T')[0];
        
        // If the due date is within the next 30 days
        if (dueDate >= currentDate && dueDate <= thirtyDaysFromNow) {
          if (cashFlowByDay.has(dueDateKey)) {
            const dayData = cashFlowByDay.get(dueDateKey);
            dayData.amount += invoice.total || 0;
            dayData.invoiceCount += 1;
                          // `Added $${(invoice.total || 0).toLocaleString()} due on ${dueDateKey}`);
          }
        }
      }
    });
    
    // Fetch and include recurring invoice projections
    try {
      const recurringTemplatesQuery = query(
        collection(db, 'invoiceTemplates'),
        where('companyId', '==', companyId),
        where('status', '==', 'active')
      );
      
      const templatesSnapshot = await getDocs(recurringTemplatesQuery);
      // `Found ${templatesSnapshot.size} active recurring templates`);
      
      templatesSnapshot.docs.forEach(templateDoc => {
        const templateData = templateDoc.data();
        
        // Calculate when the next recurring invoice would be due
        if (templateData.recurringDetails?.frequency && templateData.nextPaymentDueDate) {
          const nextDueDate = templateData.nextPaymentDueDate.toDate();
          const nextDueDateKey = nextDueDate.toISOString().split('T')[0];
          
          // If the next due date is within the next 30 days
          if (nextDueDate >= currentDate && nextDueDate <= thirtyDaysFromNow) {
            if (cashFlowByDay.has(nextDueDateKey)) {
              const dayData = cashFlowByDay.get(nextDueDateKey);
              const recurringAmount = templateData.invoiceDetails?.total || templateData.total || 0;
              dayData.amount += recurringAmount;
              dayData.invoiceCount += 1;
              dayData.type = 'recurring'; // Mark as recurring revenue
              // `Added $${recurringAmount.toLocaleString()} from recurring template due on ${nextDueDateKey}`);
            }
          }
        }
      });
    } catch (error) {
      logApiError('getDashboardMetrics.recurringTemplateProjection', error);
    }
    
    // Convert to array and calculate total
    metrics.cashFlowForecast = Array.from(cashFlowByDay.values())
      .filter(day => day.amount > 0) // Only include days with expected payments
      .sort((a, b) => a.day - b.day);
    
    // Calculate total projected cash flow for the next 30 days
    metrics.cashFlowTotal = metrics.cashFlowForecast.reduce((sum, day) => sum + day.amount, 0);
    
    // Calculate projected revenue (same as cash flow total)
    metrics.projectedRevenue = metrics.cashFlowTotal;
    
    // Calculate recurring revenue percentage
    const recurringCashFlow = metrics.cashFlowForecast
      .filter(day => day.type === 'recurring')
      .reduce((sum, day) => sum + day.amount, 0);
    
    metrics.recurringRevenuePercentage = metrics.cashFlowTotal > 0 
      ? (recurringCashFlow / metrics.cashFlowTotal) * 100 
      : 0;
      
    // `Cash flow projection: $${metrics.cashFlowTotal.toLocaleString()} total, ${metrics.cashFlowForecast.length} active days, ${metrics.recurringRevenuePercentage.toFixed(1)}% recurring`);

    // Calculate collection rate based on actual payment performance
    const totalInvoicesValue = invoices
      .filter(inv => inv.status.toLowerCase() !== 'draft' && inv.status.toLowerCase() !== 'cancelled')
      .reduce((sum, inv) => sum + (inv.total || 0), 0);
    
    const paidInvoicesValue = invoices
      .filter(inv => inv.status.toLowerCase() === 'paid')
      .reduce((sum, inv) => sum + (inv.total || 0), 0);
    
    // Collection rate is the percentage of total invoice value that has been collected
    metrics.collectionRate = totalInvoicesValue > 0 ? paidInvoicesValue / totalInvoicesValue : 0;
    
    // `Collection rate: ${(metrics.collectionRate * 100).toFixed(1)}% (${paidInvoicesValue.toLocaleString()} collected of ${totalInvoicesValue.toLocaleString()} total)`);

    // Final debug summary for Products tab
    // '=== PRODUCTS TAB DEBUG SUMMARY ===');
    // 'Total Products:', metrics.totalProducts);
    // 'Active Products:', metrics.activeProducts);
    // 'Low Stock Products:', metrics.lowStockProducts?.length || 0);
    // 'Top Products by Revenue:', metrics.topProductsByRevenue?.length || 0);
    // 'Inventory Health:', {
    // 'Product Profitability Rows:', metrics.productProfitability?.length || 0);
    // 'Top Selling Product:', metrics.topSellingProduct ? 'Found' : 'None');
    // 'Most Profitable Product:', metrics.mostProfitableProduct ? 'Found' : 'None');
    // '=== END PRODUCTS DEBUG ===');

    return metrics;
  } catch (error) {
    logApiError('getDashboardMetrics', error);
    return getEmptyDashboardMetrics();
  }
}

// Helper function to get date ranges based on timeframe
function getDateRangesByTimeframe(timeframe: string, currentDate: Date) {
  const startDate = new Date(currentDate);
  const endDate = new Date(currentDate);
  
  switch(timeframe) {
    case 'week':
      startDate.setDate(currentDate.getDate() - 7);
      break;
    case 'month':
      startDate.setMonth(currentDate.getMonth() - 1);
      break;
    case 'quarter':
      startDate.setMonth(currentDate.getMonth() - 3);
      break;
    case 'year':
      startDate.setFullYear(currentDate.getFullYear() - 1);
      break;
    default:
      startDate.setMonth(currentDate.getMonth() - 1); // Default to month
  }
  
  // Set time to start of day for startDate and end of day for endDate
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);
  
  return { startDate, endDate };
}

// Company Profile
export async function getCompanyProfile() {
  const querySnapshot = await getDocs(collection(db, 'companyProfile'));
  if (querySnapshot.docs.length > 0) {
    const doc = querySnapshot.docs[0];
    return convertTimestamps<CompanyProfile>(doc);
  }
  return null;
}

export async function updateCompanyProfile(profile: Partial<CompanyProfile>) {
  const querySnapshot = await getDocs(collection(db, 'companyProfile'));
  let docRef;
  
  if (querySnapshot.docs.length > 0) {
    docRef = doc(db, 'companyProfile', querySnapshot.docs[0].id);
    await updateDoc(docRef, {
      ...profile,
      updatedAt: serverTimestamp()
    });
  } else {
    docRef = await addDoc(collection(db, 'companyProfile'), {
      ...profile,
      updatedAt: serverTimestamp()
    });
  }
  
  return docRef.id;
}

export async function uploadCompanyLogo(file: File) {
  const storageRef = ref(storage, `company/logo/${file.name}`);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}

// Customer Invoices
export async function getCustomerInvoices(customerId: string, companyId?: string) {
  try {
    // Get the current user's company ID if not provided
    const currentUser = auth.currentUser;
    let targetCompanyId = companyId;

    if (!targetCompanyId && currentUser) {
      targetCompanyId = await getCompanyId(currentUser);
    }

    if (!targetCompanyId) {
      // 'No company ID available for customer invoices query');
      return [];
    }
    
    // 'Fetching invoices for customer:', customerId, 'company:', targetCompanyId);
    
    const querySnapshot = await getDocs(
      query(
        collection(db, 'invoices'),
        where('customerId', '==', customerId),
        where('companyId', '==', targetCompanyId),
        orderBy('createdAt', 'desc')
      )
    );
    
    const invoices = querySnapshot.docs.map(doc => convertTimestamps<Invoice>(doc));
    // `Found ${invoices.length} invoices for customer ${customerId}`);
    
    return invoices;
  } catch (error) {
    logApiError('getCustomerInvoices', error);
    return [];
  }
}

// Customer Payment Methods
export async function getCustomerPaymentMethods(customerId: string, companyId?: string) {
  try {
    // Get the current user's company ID if not provided
    const currentUser = auth.currentUser;
    let targetCompanyId = companyId;

    if (!targetCompanyId && currentUser) {
      targetCompanyId = await getCompanyId(currentUser);
    }

    if (!targetCompanyId) {
      // 'No company ID available for customer payment methods query');
      return [];
    }
    
    // 'Fetching payment methods for customer:', customerId, 'company:', targetCompanyId);
    
    const querySnapshot = await getDocs(
      query(
        collection(db, 'paymentMethods'),
        where('customerId', '==', customerId),
        where('companyId', '==', targetCompanyId),
        orderBy('createdAt', 'desc')
      )
    );
    
    // '=== FIRESTORE QUERY DEBUG ===');
    // 'Query executed successfully');
    // 'Number of documents returned:', querySnapshot.docs.length);
    // 'Query parameters:', {
    
    const paymentMethods = querySnapshot.docs.map(doc => convertTimestamps<PaymentMethod>(doc));
    // `Found ${paymentMethods.length} payment methods for customer ${customerId}`);
    
    return paymentMethods;
  } catch (error) {
    logApiError('getCustomerPaymentMethods', error);
    return [];
  }
}

// User Roles
export async function getUserRole(userId: string): Promise<UserRole | null> {
  const querySnapshot = await getDocs(
    query(collection(db, 'userRoles'), where('userId', '==', userId))
  );
  
  if (querySnapshot.docs.length > 0) {
    const doc = querySnapshot.docs[0];
    return doc.data().role as UserRole;
  }
  return null;
}

export async function createUserRole(role: Omit<UserRole, 'id' | 'createdAt' | 'updatedAt'>) {
  const docRef = await addDoc(collection(db, 'userRoles'), {
    ...role,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
}

export interface Settings {
  platformFee: number;
  defaultTaxRate: number;
}

export async function getSettings(): Promise<Settings> {
  try {
    const settingsDoc = await getDoc(doc(db, 'settings', 'global'));
    
    if (!settingsDoc.exists()) {
      return {
        platformFee: 0,
        defaultTaxRate: 0
      };
    }

    const data = settingsDoc.data();
    return {
      platformFee: data.platformFee ?? 0,
      defaultTaxRate: data.defaultTaxRate ?? 0
    };
  } catch (error) {
    logApiError('getSettings', error);
    throw new Error('Failed to fetch settings');
  }
}

export async function updateSettings(updates: Partial<Settings>): Promise<Settings> {
  try {
    const settingsRef = doc(db, 'settings', 'global');
    
    // Validate the updates
    if (updates.platformFee !== undefined && (isNaN(updates.platformFee) || updates.platformFee < 0 || updates.platformFee > 100)) {
      throw new Error('Platform fee must be a number between 0 and 100');
    }
    if (updates.defaultTaxRate !== undefined && (isNaN(updates.defaultTaxRate) || updates.defaultTaxRate < 0 || updates.defaultTaxRate > 100)) {
      throw new Error('Tax rate must be a number between 0 and 100');
    }

    await setDoc(settingsRef, {
      ...updates,
      updatedAt: serverTimestamp()
    }, { merge: true });

    // Fetch and return the updated settings
    const updatedDoc = await getDoc(settingsRef);
    const data = updatedDoc.data() || {};
    
    return {
      platformFee: data.platformFee ?? 0,
      defaultTaxRate: data.defaultTaxRate ?? 0
    };
  } catch (error) {
    logApiError('updateSettings', error);
    throw error instanceof Error ? error : new Error('Failed to update settings');
  }
}

export const fetchWithRetry = async (
  url: string,
  options: RequestInit & { retries?: number } = {}
): Promise<Response> => {
  const { retries = 3, ...fetchOptions } = options;
  let lastError: Error | null = null;
  
  // Construct full URL
  const baseUrl = getBaseUrl();
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;

  for (let i = 0; i < retries; i++) {
    try {
      const response = await fetch(fullUrl, {
        ...fetchOptions,
        headers: {
          'Content-Type': 'application/json',
          ...fetchOptions.headers,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return response;
    } catch (error) {
      lastError = error as Error;
      // `Attempt ${i + 1} failed:`, error);
      // 'Request URL:', fullUrl); // Add this for debugging
      
      // Only wait if we're going to retry
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
      }
    }
  }

  throw new Error(`Failed after ${retries} attempts: ${lastError?.message}`);
};

// Example usage for your API calls
export const api = {
  get: <T>(url: string, options?: RequestInit) =>
    fetchWithRetry(url, { ...options, method: 'GET' }).then(res => res.json() as Promise<T>),
  
  post: <T>(url: string, data: unknown, options?: RequestInit) =>
    fetchWithRetry(url, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    }).then(res => res.json() as Promise<T>),
  
  put: <T>(url: string, data: unknown, options?: RequestInit) =>
    fetchWithRetry(url, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    }).then(res => res.json() as Promise<T>),
  
  delete: <T>(url: string, options?: RequestInit) =>
    fetchWithRetry(url, { ...options, method: 'DELETE' }).then(res => res.json() as Promise<T>),
};

// Update the settings related functions
import type { CompanySettings as FirebaseCompanySettings } from '@/types/firebase';
export type CompanySettings = FirebaseCompanySettings;

export async function getCompanySettings(companyIdentifier: string) {
  // Ensure the identifier has the correct prefix
  const docId = companyIdentifier.startsWith('company_') 
    ? companyIdentifier 
    : `company_${companyIdentifier}`;
  
  const companyDoc = await getDoc(doc(db, 'companies', docId));
  if (!companyDoc.exists()) {
    throw new Error('Company settings not found');
  }
  return companyDoc.data() as CompanySettings;
}

export async function updateCompanySettings(
  companyId: string, 
  settings: Partial<CompanySettings>
): Promise<void> {
  // 'Updating company settings for', companyId, settings);
  
  // First update in companies collection
  const companiesDocRef = doc(db, 'companies', companyId);
  await updateDoc(companiesDocRef, {
    ...settings,
    updatedAt: serverTimestamp()
  });
  
  // Also update in settings collection for backward compatibility
  try {
    const settingsDocRef = doc(db, 'settings', companyId);
    const settingsDoc = await getDoc(settingsDocRef);
    
    if (settingsDoc.exists()) {
      await updateDoc(settingsDocRef, {
        ...settings,
        updatedAt: serverTimestamp()
      });
      // 'Updated settings in both collections');
    } else {
      // 'Settings document does not exist in settings collection, only updated companies collection');
    }
  } catch (settingsError) {
    // Continue without failing - companies collection is the main source
    logApiError('updateCompanySettings.settingsCollection', settingsError);
  }
}

// Products
export async function getProducts(companyId: string) {
  // `[api.getProducts] Called with companyId: '${companyId}'`); // Log entry point and parameters
  
  if (!companyId) {
    const errorMsg = 'Company ID is required to fetch products';
    // `[api.getProducts] Error: ${errorMsg}`);
    throw new Error(errorMsg);
  }

  try {
    // `[api.getProducts] Querying products for companyId: '${companyId}'`);
    const productsQuery = query(
      collection(db, 'products'),
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(productsQuery);
    // `[api.getProducts] Firestore query returned ${querySnapshot.docs.length} documents`);

    const productsData = querySnapshot.docs.map(doc => ({
      ...convertTimestamps<Product>(doc)
    }));

    // `[api.getProducts] Successfully processed ${productsData.length} products`);
    return productsData;
  } catch (error) {
    // '[api.getProducts] Error fetching products:', error);
    throw error; // Re-throw for React Query to handle
  }
}

export async function createProduct(product: Omit<Product, 'id' | 'createdAt'>) {
  try {
    // Check for existing product with the same name in this company
    const productSnapshot = await getDocs(
      query(
        collection(db, 'products'),
        where('name', '==', product.name),
        where('companyId', '==', product.companyId)
      )
    );
    
    if (!productSnapshot.empty) {
      throw new Error('A product with this name already exists. Please use a different name.');
    }

    const docRef = await addDoc(collection(db, 'products'), {
      ...product,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Return the full product object with the generated ID
    return {
      ...product,
      id: docRef.id,
      createdAt: new Date()
    } as Product;
  } catch (error) {
    // 'Error creating product:', error);
    throw error instanceof Error ? error : new Error('Failed to create product. Please try again.');
  }
}

export async function updateProduct(id: string, product: Partial<Product>) {
  try {
    // Only check for duplicates if the name is being updated
    if (product.name) {
      // Get the current product to get its companyId
      const productDoc = await getDoc(doc(db, 'products', id));
      if (!productDoc.exists()) {
        throw new Error('Product not found');
      }
      
      const currentProduct = productDoc.data() as Product;
      
      // Check for existing product with the same name (excluding current product)
      const productSnapshot = await getDocs(
        query(
          collection(db, 'products'),
          where('name', '==', product.name),
          where('companyId', '==', currentProduct.companyId),
          where(documentId(), '!=', id)
        )
      );
      
      if (!productSnapshot.empty) {
        throw new Error('Another product with this name already exists. Please use a different name.');
      }
    }

    const docRef = doc(db, 'products', id);
    await updateDoc(docRef, {
      ...product,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    logApiError('updateProduct', error);
    throw error instanceof Error ? error : new Error('Failed to update product. Please try again.');
  }
}

export async function deleteProduct(id: string) {
  try {
    await deleteDoc(doc(db, 'products', id));
  } catch (error) {
    logApiError('deleteProduct', error);
    throw new Error('Failed to delete product. Please try again.');
  }
}

// Categories
export async function getCategories(companyId = '') {
  try {
    if (!companyId) {
      const user = auth.currentUser as FirebaseUser | null;
      if (!user) throw new Error('Not authenticated');

      // Get company ID (handles impersonation)
      companyId = await getCompanyId(user);
    }
    
    // Return categories for specific company
    const querySnapshot = await getDocs(
      query(
        collection(db, 'categories'),
        where('companyId', '==', companyId),
        orderBy('createdAt', 'desc')
      )
    );
    
    return querySnapshot.docs.map(doc => ({
      ...convertTimestamps<Category>(doc)
    }));
  } catch (error) {
    logApiError('getCategories', error);
    return [];
  }
}

export async function createCategory(category: Omit<Category, 'id' | 'createdAt'>) {
  try {
    // Check for existing category with the same name in this company
    const categorySnapshot = await getDocs(
      query(
        collection(db, 'categories'),
        where('name', '==', category.name),
        where('companyId', '==', category.companyId)
      )
    );
    
    if (!categorySnapshot.empty) {
      throw new Error('A category with this name already exists. Please use a different name.');
    }

    const docRef = await addDoc(collection(db, 'categories'), {
      ...category,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Return the full category object with the generated ID
    return {
      ...category,
      id: docRef.id,
      createdAt: new Date()
    } as Category;
  } catch (error) {
    logApiError('createCategory', error);
    throw error instanceof Error ? error : new Error('Failed to create category. Please try again.');
  }
}

export async function deleteCategory(id: string) {
  try {
    // Check if any products are using this category
    const productsWithCategory = await getDocs(
      query(
        collection(db, 'products'),
        where('category', '==', id),
        limit(1)
      )
    );
    
    if (!productsWithCategory.empty) {
      throw new Error('Cannot delete category that is in use by products');
    }
    
    await deleteDoc(doc(db, 'categories', id));
  } catch (error) {
    logApiError('deleteCategory', error);
    throw error instanceof Error ? error : new Error('Failed to delete category. Please try again.');
  }
}

// Add this function to get company data
export async function getCompany(companyId: string) {
  try {
    const docId = companyId.startsWith('company_') ? companyId : `company_${companyId}`;
    const companyDoc = await getDoc(doc(db, 'companies', docId));
    
    if (!companyDoc.exists()) {
      throw new Error('Company not found');
    }
    
    return convertTimestamps(companyDoc);
  } catch (error) {
    logApiError('getCompany', error);
    throw new Error('Failed to fetch company data');
  }
}

export async function getPublicInvoice(invoiceId: string) {
  try {
    if (!invoiceId) {
      // 'No invoice ID provided');
      throw new Error('Invoice ID is required');
    }

    // `[getPublicInvoice] Fetching invoice data for ID: ${invoiceId}`);

    // Determine the appropriate base URL depending on environment and configuration
    let baseUrl = '';
    let isDevelopmentEnvironment = false;
    
    // Check for environment variables first
    const envApiUrl = import.meta.env.VITE_API_URL || '';
    
    if (typeof window !== 'undefined') {
      // Client-side environment
      const currentHost = window.location.hostname;
      
      // Check if we're in development
      isDevelopmentEnvironment = 
        currentHost === 'localhost' || 
        currentHost === '127.0.0.1' || 
        currentHost.includes('.local');
      
      // If current hostname is the production domain, we need to use that domain
      if (currentHost === 'www.secured-payment-processing.com') {
        // Since we're seeing 502 errors at the production URL, let's try the Next.js API directly
        // which is served from the same domain
        baseUrl = window.location.origin;
        // `[getPublicInvoice] Using current origin for API on production domain: ${baseUrl}`);
      } else if (envApiUrl) {
        // If we have an environment variable, use that first
        baseUrl = envApiUrl;
        // `[getPublicInvoice] Using API URL from environment variable: ${baseUrl}`);
      } else {
        // Default to current origin (works for development)
        baseUrl = window.location.origin;
        // `[getPublicInvoice] Using current origin: ${baseUrl}`);
      }
    } else {
      // Server-side environment
      baseUrl = envApiUrl;
      // `[getPublicInvoice] Server-side environment, using API URL from environment: ${baseUrl || 'empty base URL'}`);
    }
    
    // Construct the API URL - make sure we don't have double slashes in the path
    let apiUrl = baseUrl;
    if (!apiUrl.endsWith('/')) {
      apiUrl += '/';
    }
    apiUrl += `api/public-invoice/${invoiceId}`;
    
    // `[getPublicInvoice] Full API URL: ${apiUrl}`);
    
    // Add retries and timeout for better reliability
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    try {
      // Skip API call if we know we're using the secured-payment-processing.com 
      // domain in development mode, as it will definitely fail
      const isUsingPlaceholderDomain = 
        apiUrl.includes('secured-payment-processing.com') && 
        isDevelopmentEnvironment;
        
      if (isUsingPlaceholderDomain) {
        // '[getPublicInvoice] Skipping external API call to placeholder domain in development');
        throw new Error('SKIP_EXTERNAL_API');
      }
        
      // Use a try-catch specifically for the fetch operation
      const response = await fetch(apiUrl, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'X-Requested-With': 'XMLHttpRequest' // Some servers use this to detect AJAX requests
        },
        // Add credentials to handle any cookies/auth if needed
        credentials: 'include'
      });
      
      clearTimeout(timeoutId);
      
      // `[getPublicInvoice] Response status: ${response.status}`);
      
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          const error = await response.json();
          // Use less alarming message in development
          if (isDevelopmentEnvironment) {
            // '[getPublicInvoice] API returned error, falling back to direct database access');
          } else {
            // '[getPublicInvoice] JSON error response:', error);
          }
          throw new Error(error.error || `Failed to fetch invoice data: ${response.status}`);
        } else {
          const textContent = await response.text();
          
          // If we got HTML response with CloudFlare error, it's likely a network issue
          if (textContent.includes('cloudflare') || response.status === 502 || response.status === 504) {
            if (isDevelopmentEnvironment) {
              // '[getPublicInvoice] CloudFlare or gateway error, falling back to direct database access');
            } else {
              // '[getPublicInvoice] CloudFlare or gateway error detected');
            }
            throw new Error(`Network error: The API server is currently unreachable (status ${response.status})`);
          }
          
          if (isDevelopmentEnvironment) {
            // '[getPublicInvoice] Non-JSON error response, falling back to direct database access');
          } else {
            // '[getPublicInvoice] Non-JSON error response:', textContent.substring(0, 200) + '...');
          }
          throw new Error(`Server error: ${response.status} - The server returned an invalid response`);
        }
      }
      
      // '[getPublicInvoice] Successfully received response, parsing JSON');
      
      const data = await response.json();
      
      // Validate the response data
      if (!data) {
        // '[getPublicInvoice] Empty response data');
        throw new Error('Empty response from server');
      }
      
      if (!data.invoice) {
        // '[getPublicInvoice] No invoice in response data', data);
        throw new Error('Invoice not found in server response');
      }
      
      // '[getPublicInvoice] Successfully parsed response data');
      return data;
    } catch (fetchError: unknown) {
      clearTimeout(timeoutId);
      
      // Special case for skipping external API
      if (fetchError instanceof Error && fetchError.message === 'SKIP_EXTERNAL_API') {
        // '[getPublicInvoice] Skipped external API call, proceeding to direct database access');
        throw fetchError;
      }
      
      if (fetchError instanceof Error && fetchError.name === 'AbortError') {
        if (isDevelopmentEnvironment) {
          // '[getPublicInvoice] Request timed out, falling back to direct database access');
        } else {
          // '[getPublicInvoice] Request timed out after 10 seconds');
        }
        throw new Error('Request timed out. Please try again.');
      }
      
      if (isDevelopmentEnvironment) {
        // In development, just log as info to avoid alarming errors in console
        // '[getPublicInvoice] API fetch unsuccessful, falling back to direct database access');
      } else {
        // '[getPublicInvoice] Fetch error:', fetchError);
      }
      
      // If the error indicates network problems, clarify this for the user
      if (fetchError instanceof Error && 
          (fetchError.message.includes('Network') || 
           fetchError.message.includes('Failed to fetch'))) {
        throw new Error('Network error: Could not connect to invoice server. Please check your internet connection.');
      }
      
      throw fetchError;
    }
  } catch (error) {
    // In development, don't log most API errors as they're expected
    const isDevelopmentEnvironment = typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' || 
      window.location.hostname === '127.0.0.1' || 
      window.location.hostname.includes('.local')
    );
    
    if (!isDevelopmentEnvironment || 
        !(error instanceof Error) || 
        (!error.message.includes('Invoice not found') && 
         !error.message.includes('Network error') &&
         !error.message.includes('SKIP_EXTERNAL_API'))) {
      // '[getPublicInvoice] Error fetching public invoice:', error);
    }
    
    throw error instanceof Error 
      ? error 
      : new Error('Failed to fetch invoice. Please try again.');
  }
}

// Utility function to migrate existing invoices to use payment keys
export async function migrateInvoicesToUsePaymentKeys() {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const companyId = await getCompanyId(user);
    
    // Find all invoices without payment keys for this company
    const querySnapshot = await getDocs(
      query(
        collection(db, 'invoices'),
        where('companyId', '==', companyId),
        where('paymentKey', '==', null)
      )
    );
    
    // No invoices without payment keys
    if (querySnapshot.empty) {
      // 'No invoices require migration - all invoices already have payment keys');
      return { migrated: 0, total: 0 };
    }
    
    // `Found ${querySnapshot.docs.length} invoices without payment keys, updating...`);
    
    // Update each invoice with a new payment key
    const updatePromises = querySnapshot.docs.map(async (docSnapshot) => {
      const paymentKey = generatePaymentKey();
      await updateDoc(doc(db, 'invoices', docSnapshot.id), {
        paymentKey,
        updatedAt: serverTimestamp()
      });
      return docSnapshot.id;
    });
    
    // Wait for all updates to complete
    const updatedIds = await Promise.all(updatePromises);
    
    // `Successfully migrated ${updatedIds.length} invoices to use payment keys`);
    return { 
      migrated: updatedIds.length,
      total: querySnapshot.docs.length,
      success: true 
    };
  } catch (error) {
    logApiError('migrateInvoicesToPaymentKeys', error);
    throw new Error('Failed to migrate invoices. Please try again.');
  }
}

// User Management API Functions
export async function getUsers(companyId: string) {
  const usersSnapshot = await getDocs(
    query(collection(db, 'users'), where('companyId', '==', companyId))
  );
  
  return usersSnapshot.docs.map(doc => ({
    uid: doc.id,
    ...doc.data()
  })) as User[];
}

export async function getUserById(userId: string) {
  const userDoc = await getDoc(doc(db, 'users', userId));
  if (!userDoc.exists()) {
    throw new Error('User not found');
  }
  
  return { uid: userDoc.id, ...userDoc.data() } as User;
}

export async function createUser(userData: Partial<User> & { password: string }) {
  try {
    // Create the auth user
    const { email, password } = userData;
    if (!email || !password) {
      throw new Error('Email and password are required');
    }
    
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const uid = userCredential.user.uid;
    
    // Remove password from userData before storing in Firestore
    const { password: _, ...userDataWithoutPassword } = userData;
    
    // Store user data in Firestore
    await setDoc(doc(db, 'users', uid), {
      ...userDataWithoutPassword,
      uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isActive: true
    });
    
    // Send password reset email so user can set their own password
    await sendPasswordResetEmail(auth, email);
    
    return { uid, ...userDataWithoutPassword };
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
}

export async function updateUser(userId: string, userData: Partial<User>) {
  const userDocRef = doc(db, 'users', userId);
    
    // If roleId is being updated, check if it's an Admin role
    if (userData.roleId) {
      try {
        const roleDoc = await getDoc(doc(db, 'roles', userData.roleId));
        if (roleDoc.exists()) {
          const roleData = roleDoc.data();
          // If the role name is Admin (case insensitive), set the user's role field to 'admin'
          if (roleData.name && roleData.name.toLowerCase() === 'admin') {
            userData.role = 'admin';
          }
        }
      } catch (_error) {
        // Continue with the update even if this check fails
      }
    }
    
    await updateDoc(userDocRef, {
      ...userData,
      updatedAt: serverTimestamp()
    });
    
    return true;
}

export async function deleteUser(userId: string) {
  // Get the current user for checking permissions
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in to delete a user');
    }

    // Get the company ID (handles impersonation)
    const companyId = await getCompanyId(currentUser);

    // Get the current user's data from Firestore (or impersonated user)
    const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
    if (!currentUserDoc.exists()) {
      throw new Error('Current user data not found');
    }
    const currentUserData = currentUserDoc.data() as User;
    // Override companyId if impersonating
    if (companyId !== currentUserData.companyId) {
      currentUserData.companyId = companyId;
    }

    // Get the user to be deleted
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    const userData = userDoc.data() as User;

    // Prevent deleting yourself
    if (userId === currentUser.uid) {
      throw new Error('You cannot delete your own account');
    }

    // Ensure both users are in the same company
    if (userData.companyId !== currentUserData.companyId) {
      throw new Error('You cannot delete users from another company');
    }

    // Ensure the current user is an admin
    if (currentUserData.role !== 'admin' && currentUserData.role !== 'super_admin') {
      throw new Error('Only administrators can delete users');
    }

    // If the user to be deleted is an admin, check if they're the last admin
    if (userData.role === 'admin') {
      // Count admin users in the company
      const adminUsersQuery = query(
        collection(db, 'users'),
        where('companyId', '==', userData.companyId),
        where('role', '==', 'admin'),
        where('isActive', '==', true)
      );
      
      const adminUsersSnapshot = await getDocs(adminUsersQuery);
      const adminCount = adminUsersSnapshot.size;
      
      if (adminCount <= 1) {
        throw new Error('Cannot delete the last admin user. Companies must maintain at least one admin user.');
      }
    }

    // All checks passed, delete the user document
    await deleteDoc(doc(db, 'users', userId));
    
    return { success: true, message: 'User deleted successfully' };
}

// Role Management API Functions
export async function getRoles(companyId: string) {
  const rolesSnapshot = await getDocs(
    query(collection(db, 'roles'), where('companyId', '==', companyId))
  );
  
  return rolesSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Role[];
}

export async function getRoleById(roleId: string) {
  const roleDoc = await getDoc(doc(db, 'roles', roleId));
  if (!roleDoc.exists()) {
    throw new Error('Role not found');
  }
  
  return { id: roleDoc.id, ...roleDoc.data() } as Role;
}

export async function createRole(roleData: Partial<Role>) {
  try {
    // 'Creating role with data:', JSON.stringify(roleData, null, 2));
    
    // Validate required fields
    if (!roleData.name) {
      // 'Role name is required');
      throw new Error('Role name is required');
    }
    
    if (!roleData.companyId) {
      // 'Company ID is required');
      throw new Error('Company ID is required');
    }
    
    if (!roleData.createdBy) {
      // 'createdBy field is required');
      throw new Error('createdBy field is required');
    }
    
    // Make a copy of the role data to ensure we don't have circular references
    const roleCopy = {
      ...roleData,
      permissions: JSON.parse(JSON.stringify(roleData.permissions)),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // 'Processed role data:', JSON.stringify(roleCopy, null, 2));
    
    const roleRef = await addDoc(collection(db, 'roles'), roleCopy);
    // 'Role created with ID:', roleRef.id);
    
    return { id: roleRef.id, ...roleData };
  } catch (error) {
    // 'Error creating role:', error);
    if (error instanceof Error) {
      // 'Error message:', error.message);
      // 'Error stack:', error.stack);
    }
    throw error;
  }
}

export async function updateRole(roleId: string, roleData: Partial<Role>) {
  try {
    const roleDocRef = doc(db, 'roles', roleId);
    
    await updateDoc(roleDocRef, {
      ...roleData,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error updating role:', error);
    throw error;
  }
}

export async function deleteRole(roleId: string) {
  // First fetch the role to check if it's a system role
    const roleDoc = await getDoc(doc(db, 'roles', roleId));
    
    if (roleDoc.exists()) {
      const roleData = roleDoc.data();
      // Check if it's the Admin role by name or explicitly marked as a system role
      const isAdminRole = roleData.name && roleData.name.toLowerCase() === 'admin';
      const isSystemRole = roleData.isSystemRole || isAdminRole;
      
      if (isSystemRole) {
        throw new Error('System roles cannot be deleted');
      }
    }
    
    await deleteDoc(doc(db, 'roles', roleId));
    return true;
}

/**
 * Creates default roles for a new company
 */
export async function createDefaultRoles(companyId: string, createdBy: string) {
  const defaultRoles = [
      {
        name: 'Administrator',
        description: 'Full access to all features',
        companyId,
        permissions: {
          dashboard: true,
          invoices: { canView: true, canCreate: true, canEdit: true, canDelete: true },
          customers: { canView: true, canCreate: true, canEdit: true, canDelete: true },
          products: { canView: true, canCreate: true, canEdit: true, canDelete: true },
          transactions: { canView: true, canCreate: true, canEdit: true, canDelete: true },
          users: { canView: true, canCreate: true, canEdit: true, canDelete: true },
          settings: true,
          virtualTerminal: true
        },
        isDefault: true,
        createdBy,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        name: 'Manager',
        description: 'Can access all menus except settings',
        companyId,
        permissions: {
          dashboard: true,
          invoices: { canView: true, canCreate: true, canEdit: true, canDelete: true },
          customers: { canView: true, canCreate: true, canEdit: true, canDelete: true },
          products: { canView: true, canCreate: true, canEdit: true, canDelete: true },
          transactions: { canView: true, canCreate: true, canEdit: true, canDelete: true },
          users: { canView: true, canCreate: true, canEdit: true, canDelete: true },
          settings: false,
          virtualTerminal: true
        },
        isDefault: true,
        createdBy,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        name: 'Employee',
        description: 'Can only access the Invoices menu',
        companyId,
        permissions: {
          dashboard: true,
          invoices: { canView: true, canCreate: true, canEdit: true, canDelete: true },
          customers: { canView: false, canCreate: false, canEdit: false, canDelete: false },
          products: { canView: false, canCreate: false, canEdit: false, canDelete: false },
          transactions: { canView: false, canCreate: false, canEdit: false, canDelete: false },
          users: { canView: false, canCreate: false, canEdit: false, canDelete: false },
          settings: false,
          virtualTerminal: false
        },
        isDefault: true,
        createdBy,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
    ];

    // Create the default roles
    for (const role of defaultRoles) {
      try {
        const roleRef = doc(collection(db, 'roles'));
        await setDoc(roleRef, role);
        // `Created default role: ${role.name}`);
      } catch (error) {
        logApiError('createDefaultRoles', error);
      }
    }

    return true;
}

// Add a new function to get a role by name
export async function getRoleByName(companyId: string, roleName: string) {
  const rolesSnapshot = await getDocs(
      query(
        collection(db, 'roles'), 
        where('companyId', '==', companyId),
        where('name', '==', roleName)
      )
    );
    
    if (rolesSnapshot.empty) {
      return null;
    }
    
    const doc = rolesSnapshot.docs[0];
    return { id: doc.id, ...doc.data() } as Role;
}

/**
 * Check if a user can be deleted
 * @param userId - The ID of the user to check
 * @returns A result object indicating if the user can be deleted and why/why not
 */
export async function canDeleteUser(userId: string): Promise<{ canDelete: boolean; reason?: string }> {
  try {
    // Get the current user for checking permissions
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return { canDelete: false, reason: 'You must be logged in to delete a user' };
    }

    // Don't allow deleting yourself
    if (userId === currentUser.uid) {
      return { canDelete: false, reason: 'You cannot delete your own account' };
    }

    // Get the company ID (handles impersonation)
    const companyId = await getCompanyId(currentUser);

    // Get the current user's data from Firestore (or impersonated user)
    const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
    if (!currentUserDoc.exists()) {
      return { canDelete: false, reason: 'Current user data not found' };
    }
    const currentUserData = currentUserDoc.data() as User;
    // Override companyId if impersonating
    if (companyId !== currentUserData.companyId) {
      currentUserData.companyId = companyId;
    }

    // Get the user to be deleted
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return { canDelete: false, reason: 'User not found' };
    }
    const userData = userDoc.data() as User;

    // Ensure both users are in the same company
    if (userData.companyId !== currentUserData.companyId) {
      return { canDelete: false, reason: 'You cannot delete users from another company' };
    }

    // Ensure the current user is an admin
    if (currentUserData.role !== 'admin' && currentUserData.role !== 'super_admin') {
      return { canDelete: false, reason: 'Only administrators can delete users' };
    }

    // If the user to be deleted is an admin, check if they're the last admin
    if (userData.role === 'admin') {
      // Count admin users in the company
      const adminUsersQuery = query(
        collection(db, 'users'),
        where('companyId', '==', userData.companyId),
        where('role', '==', 'admin'),
        where('isActive', '==', true)
      );
      
      const adminUsersSnapshot = await getDocs(adminUsersQuery);
      const adminCount = adminUsersSnapshot.size;
      
      if (adminCount <= 1) {
        return { 
          canDelete: false, 
          reason: 'Cannot delete the last admin user. Companies must maintain at least one admin user.' 
        };
      }
    }

    // All checks passed
    return { canDelete: true };
  } catch (error) {
    // 'Error checking if user can be deleted:', error);
    return { 
      canDelete: false, 
      reason: error instanceof Error ? error.message : 'An unknown error occurred' 
    };
  }
}

// Helper function to return empty dashboard metrics when data can't be retrieved
function getEmptyDashboardMetrics() {
  return {
    totalRevenue: 0,
    totalOutstanding: 0,
    outstandingAmount: 0,
    outstandingInvoices: 0,
    overdueAmount: 0,
    overdueInvoices: 0,
    paidThisMonth: 0,
    totalInvoices: 0,
    totalCustomers: 0,
    totalProducts: 0,
    activeProducts: 0,
    lowStockProducts: [] as Product[],
    revenueGrowth: 0,
    collectionRate: 0,
    projectedRevenue: 0,
    recurringRevenuePercentage: 0,
    aging30Days: 0,
    aging60Days: 0,
    aging90Days: 0,
    aging90PlusDays: 0,
    newCustomers: 0,
    averagePurchase: 0,
    averagePurchaseGrowth: 0,
    retentionRate: 0,
    repeatCustomersCount: 0,
    lifetimeValue: 0,
    lifetimeValueGrowth: 0,
    averageMargin: 0,
    topSellingProduct: null,
    mostProfitableProduct: null,
    averagePaymentTime: 0,
    paymentTimeChange: 0,
    paymentSuccessRate: 0,
    totalTransactions: 0,
    processingFeeRatio: 0.029,
    processingFeeRatioChange: 0,
    recentActivity: [],
    revenueByMonth: [],
    invoiceStatusDistribution: [],
    cashFlowForecast: [],
    topCustomers: [],
    customerTypeDistribution: [],
    customerActivity: [],
    customerPaymentBehavior: [],
    revenueByCategory: [] as { category: string; revenue: number }[],
    profitMargins: [] as { name: string; margin: number; profit: number }[],
    overallMargin: 0,
    totalProfit: 0,
    topProductsByRevenue: [],
    inventoryHealthDistribution: [],
    productProfitability: [],
    agedReceivables: [],
    totalAgedReceivables: {
      current: 0,
      days30: 0,
      days60: 0,
      days90: 0,
      days90plus: 0,
      total: 0
    },
    invoiceProcessingTime: [],
    paymentProcessing: [],
    paymentMethodDistribution: {
      creditCard: 0,
      ach: 0
    },
    recurringInvoicesCount: 0,
    oneTimeInvoicesCount: 0,
    cashFlowTotal: 0,
    paymentFailureReasons: [] as any[],
    paymentFailureInsights: [] as any[],
    paymentRecommendations: [] as any[],
    alerts: [] as any[],
    totalSalesTax: 0,
    totalPlatformFees: 0,
    totalCreditCardPayments: 0,
    totalACHPayments: 0,
    paymentBehaviorTrend: [] as { date: string; avgPaymentTime: number }[],
    customerPaymentTrend: {} as Record<string, Array<{ date: string; daysToPayment: number }>>
  };
}

/**
 * Sends an invoice email to the customer and updates the invoice status to 'sent'
 */
export async function sendInvoice(invoiceId: string, emailSubject: string, emailBody: string) {
  try {
    // Get the invoice first
    const invoice = await getInvoice(invoiceId);
    if (!invoice) {
      throw new Error('Invoice not found');
    }

    // Construct the API endpoint for sending the email
    const endpoint = `${getApiBaseUrl()}/api/email`;

    // Make the API request
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        invoice,
        subject: emailSubject,
        template: emailBody, // This is the actual email body content from the form
        isCustomContent: true // Add a flag to indicate this is custom content
      }),
    });

    if (!response.ok) {
      // First try to parse as JSON if possible
      let errorMessage = 'Failed to send invoice';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (_jsonError) {
        // If can't parse as JSON, get raw text
        try {
          const responseText = await response.text();
          errorMessage = responseText || `Failed to send invoice: Status ${response.status}`;
        } catch (textError) {
          logApiError('sendInvoice.readErrorResponse', textError);
        }
      }
      throw new Error(errorMessage);
    }

    // Parse the response
    const responseData = await response.json();

    // Update the invoice status to 'sent' if it was in 'draft' or 'pending' state
    if (invoice.status === 'draft' || invoice.status === 'pending') {
      await updateInvoice(invoiceId, { status: 'sent' });
    }

    return responseData;
  } catch (error) {
    console.error('Error sending invoice:', error);
    throw error;
  }
}

// Function to save directly as an invoice template (for recurring invoices)
export async function createInvoiceTemplate(
  template: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'> & {
    emailTemplate?: {
      subject: string;
      body: string;
    },
    schedulePreview?: {
      frequency: string;
      firstInvoiceDate: string;
      firstPaymentDue: string;
      secondInvoiceDate: string;
      secondPaymentDue: string;
      invoiceGenerationDays: number;
    }
  }
) {
  // Get the reference to the templates collection
  const templateRef = collection(db, 'invoiceTemplates');
  
  // Check if paymentMethods includes ACH
  const hasACH = template.paymentMethods && template.paymentMethods.includes('ach');
  
  // Convert invoice data to template format
  const templateData: any = {
    baseInvoiceId: null, // No base invoice since this is direct template creation
    companyId: template.companyId,
    customerName: template.customerName,
    customerEmail: template.customerEmail,
    customerDetails: {
      id: template.customerId,
      name: template.customerName,
      email: template.customerEmail,
      address: template.customerAddress,
      street: template.street,
      city: template.city,
      state: template.state,
      zip: template.zip
    },
    invoiceNumber: template.invoiceNumber,
    invoiceDetails: {
      subtotal: template.subtotal,
      taxRate: template.taxRate,
      taxAmount: template.taxAmount,
      platformFee: template.platformFee,
      total: template.total,
      notes: template.notes || ''
    },
    // Only add emailTemplate if provided
    ...(template.emailTemplate ? { emailTemplate: template.emailTemplate } : {}),
    lineItems: template.items || [],
    // Add payment methods from template directly to ensure ACH and other payment methods are saved
    paymentMethods: template.paymentMethods || ['credit_card'],
    autoPayEnabled: template.autoPayEnabled === true,
    recurringDetails: {
      ...(template.recurringDetails || {
        frequency: 'monthly',
        isActive: true
      }),
      // Add enableACH flag when ACH is in paymentMethods
      enableACH: hasACH
    },
    // Add schedule preview data if provided
    ...(template.schedulePreview ? { schedulePreview: template.schedulePreview } : {}),
    status: 'active',
    hasBeenActivated: template.recurringDetails?.isActive !== false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: 'system'
  };
  
  // Add the template to the invoiceTemplates collection
  const docRef = await addDoc(templateRef, templateData);
  
  // Get the created document to return the full template data
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) {
    throw new Error('Failed to create invoice template');
  }
  
  // Return the created template
  return {
    id: docSnap.id,
    ...docSnap.data()
  };
}

// Function to create invoice from template
export async function createInvoiceFromTemplate(templateId: string, userId: string) {
  // Get the template
    const templateRef = doc(db, 'invoiceTemplates', templateId);
    const templateSnap = await getDoc(templateRef);
    
    if (!templateSnap.exists()) {
      throw new Error('Template not found');
    }
    
    const templateData = templateSnap.data();
    
    // Generate new invoice number
    const invoiceNumber = `INV-${Date.now()}`;

    // Handle payment methods
    let paymentMethods = templateData.paymentMethods || ['credit_card'];
    // If ACH is enabled in the template, ensure it's in the payment methods
    if (templateData.recurringDetails?.enableACH) {
      if (!paymentMethods.includes('ach')) {
        paymentMethods = [...paymentMethods, 'ach'];
      }
    }
    
    // Create the invoice data
    const invoiceData = {
      ...templateData,
      invoiceNumber,
      platformFee: 0, // Always set platform fee to 0 for recurring invoices
      paymentMethods, // Use the processed payment methods
      status: 'pending', // Always set to pending for new invoices
      invoiceType: 'standard', // Generated invoices are standard invoices
      autoPayEnabled: templateData.autoPayEnabled === true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userId,
      // Add metadata to link this invoice to the template
      metadata: {
        isRecurring: true,
        recurringTemplateId: templateId,
        ...(templateData.metadata || {})
      },
      // Include stored payment method from template if available
      ...(templateData.storedPaymentMethod && {
        storedPaymentMethod: templateData.storedPaymentMethod
      }),
      activities: [{
        id: crypto.randomUUID(),
        type: 'created',
        timestamp: new Date().toISOString(),
        description: 'Invoice created from template',
        performedBy: userId,
        metadata: {
          templateId
        }
      }]
    };
    
    // Create the invoice
    const invoiceRef = await addDoc(collection(db, 'invoices'), invoiceData);
    
    // Add this invoice to the template's tracking
    try {
      await addInvoiceToTemplateTracking(templateId, {
        invoiceId: invoiceRef.id,
        invoiceNumber: invoiceNumber,
        dueDate: templateData.dueDate || new Date().toISOString().split('T')[0],
        amount: templateData.invoiceDetails?.total || templateData.total || 0,
        status: 'pending'
      });
      // `Added invoice ${invoiceRef.id} to template ${templateId} tracking`);
    } catch (trackingError) {
      // Don't fail the invoice creation if tracking fails
      logApiError('createInvoiceFromTemplate.tracking', trackingError);
    }
    
    return {
      id: invoiceRef.id,
      ...invoiceData
    };
}

/**
 * Updates a recurring template with payment method information after first payment
 */
export async function updateTemplateWithPaymentMethod(
  templateId: string,
  paymentMethodData: {
    paymentMethodId: string;
    nmiCustomerId: string;
    type: 'credit_card' | 'ach';
    last4: string;
    cardType?: string;
    expiryMonth?: string;
    expiryYear?: string;
    accountType?: string;
    routingNumberLast4?: string;
    nickName?: string;
  }
) {
  const templateRef = doc(db, 'invoiceTemplates', templateId);
    
    await updateDoc(templateRef, {
      storedPaymentMethod: {
        ...paymentMethodData,
        capturedAt: serverTimestamp()
      },
      updatedAt: serverTimestamp()
    });
    
    // `Updated template ${templateId} with payment method`);
}

/**
 * Adds a generated invoice record to the template's tracking
 */
export async function addInvoiceToTemplateTracking(
  templateId: string,
  invoiceData: {
    invoiceId: string;
    invoiceNumber: string;
    dueDate: string;
    amount: number;
    status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'pending';
  }
) {
  // '=== addInvoiceToTemplateTracking DEBUG START ===');
    // 'Template ID:', templateId);
    // 'Invoice Data:', invoiceData);
    
    const templateRef = doc(db, 'invoiceTemplates', templateId);
    const templateSnap = await getDoc(templateRef);
    
    if (!templateSnap.exists()) {
      // `Template not found with ID: ${templateId}`);
      throw new Error('Template not found');
    }
    
    // 'Template found, document ID:', templateSnap.id);
    // 'Template data:', templateSnap.data());
    
    const templateData = templateSnap.data();
    const existingInvoices = templateData.generatedInvoices || [];
    
    // 'Existing invoices count:', existingInvoices.length);
    
    // Format the due date to MM/DD/YYYY
    let formattedDueDate = invoiceData.dueDate;
    if (invoiceData.dueDate && invoiceData.dueDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = invoiceData.dueDate.split('-');
      formattedDueDate = `${month}/${day}/${year}`;
    }
    
    // Add new invoice to the tracking array
    const newInvoiceEntry = {
      ...invoiceData,
      dueDate: formattedDueDate,
      generatedAt: new Date() // Use regular Date instead of serverTimestamp()
    };
    
    const updatedInvoices = [...existingInvoices, newInvoiceEntry];
    
    // Calculate totals
    const totalGenerated = {
      count: updatedInvoices.length,
      totalAmount: updatedInvoices.reduce((sum, inv) => sum + inv.amount, 0),
      paidCount: updatedInvoices.filter(inv => inv.status === 'paid').length,
      paidAmount: updatedInvoices
        .filter(inv => inv.status === 'paid')
        .reduce((sum, inv) => sum + inv.amount, 0),
      lastUpdated: serverTimestamp()
    };
    
    // 'Calculated totals:', {
    
    await updateDoc(templateRef, {
      generatedInvoices: updatedInvoices,
      totalGenerated,
      updatedAt: serverTimestamp()
    });
    
    // `Successfully added invoice ${invoiceData.invoiceId} to template ${templateId} tracking`);
    // '=== addInvoiceToTemplateTracking DEBUG END ===');
}

/**
 * Updates an invoice's status in the template tracking
 */
export async function updateInvoiceStatusInTemplate(
  templateId: string,
  invoiceId: string,
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'pending',
  paymentData?: {
    paymentDate: Date;
    transactionId: string;
  }
) {
  const templateRef = doc(db, 'invoiceTemplates', templateId);
    const templateSnap = await getDoc(templateRef);
    
    if (!templateSnap.exists()) {
      throw new Error('Template not found');
    }
    
    const templateData = templateSnap.data();
    const existingInvoices = templateData.generatedInvoices || [];
    
    // Find and update the specific invoice
    const updatedInvoices = existingInvoices.map((invoice: any) => {
      if (invoice.invoiceId === invoiceId) {
        return {
          ...invoice,
          status,
          ...(paymentData && {
            paymentDate: paymentData.paymentDate,
            transactionId: paymentData.transactionId
          })
        };
      }
      return invoice;
    });
    
    // Recalculate totals
    const totalGenerated = {
      count: updatedInvoices.length,
      totalAmount: updatedInvoices.reduce((sum: number, inv: any) => sum + inv.amount, 0),
      paidCount: updatedInvoices.filter((inv: any) => inv.status === 'paid').length,
      paidAmount: updatedInvoices
        .filter((inv: any) => inv.status === 'paid')
        .reduce((sum: number, inv: any) => sum + inv.amount, 0),
      lastUpdated: serverTimestamp()
    };
    
    await updateDoc(templateRef, {
      generatedInvoices: updatedInvoices,
      totalGenerated,
      updatedAt: serverTimestamp()
    });
    
    // `Updated invoice ${invoiceId} status to ${status} in template ${templateId}`);
}

/**
 * Gets all invoices generated from a specific template, ensuring data is current.
 */
export async function getTemplateGeneratedInvoices(templateId: string) {
  try {
    const templateRef = doc(db, 'invoiceTemplates', templateId);
    const templateSnap = await getDoc(templateRef);

    if (!templateSnap.exists()) {
      throw new Error('Template not found');
    }

    const templateData = templateSnap.data();
    const storedInvoices = templateData.generatedInvoices || [];
    const refreshedInvoices = [];

    // To store promises for fetching actual invoice data
    const invoiceFetchPromises = [];

    for (const storedInvoice of storedInvoices) {
      if (storedInvoice.invoiceId) {
        invoiceFetchPromises.push(
          getDoc(doc(db, 'invoices', storedInvoice.invoiceId)).then(invoiceDoc => ({
            storedInvoice,
            invoiceDoc
          }))
        );
      }
    }

    const fetchedInvoiceResults = await Promise.all(invoiceFetchPromises);

    for (const result of fetchedInvoiceResults) {
      const { storedInvoice, invoiceDoc } = result;
      if (invoiceDoc.exists()) {
        const currentInvoiceData = invoiceDoc.data() as Invoice; // Cast to Invoice type
        let paymentDate = storedInvoice.paymentDate || null;

        // If invoice is paid and paymentDate isn't in stored tracking, try to find it
        if (currentInvoiceData.status === 'paid' && !paymentDate) {
          // Option 1: Check paymentTransactionId (if you have this field on invoice)
          if (currentInvoiceData.paymentTransactionId) {
            try {
              const transactionDoc = await getDoc(doc(db, 'transactions', currentInvoiceData.paymentTransactionId));
              if (transactionDoc.exists()) {
                paymentDate = transactionDoc.data().date; // Assuming transaction has a 'date' field
              }
            } catch (_error) {
              // `Error fetching transaction ${currentInvoiceData.paymentTransactionId}:`, _error);
            }
          }
          // Option 2: Check activities (more robust if paymentTransactionId isn't always set)
          if (!paymentDate && currentInvoiceData.activities) {
            const paidActivity = currentInvoiceData.activities.find(act => act.type === 'paid' || (act.metadata?.newStatus === 'paid'));
            if (paidActivity) {
              paymentDate = paidActivity.timestamp; // Assuming activity timestamp is the payment date
            }
          }
        }

        refreshedInvoices.push({
          ...storedInvoice, // Keep original generatedAt, invoiceNumber, etc.
          invoiceId: invoiceDoc.id, // Ensure ID is from the actual doc
          status: currentInvoiceData.status,
          amount: currentInvoiceData.total, // Use current total amount
          dueDate: currentInvoiceData.dueDate, // Use current due date
          // Convert paymentDate to a serializable format if it's a Firestore Timestamp or Date object
          paymentDate: paymentDate ? (paymentDate.toDate ? paymentDate.toDate().toISOString() : new Date(paymentDate).toISOString()) : null,
        });
      } else {
        // Invoice was deleted, so we don't add it to refreshedInvoices
        // `Invoice ${storedInvoice.invoiceId} from template ${templateId} was deleted.`);
      }
    }

    // Recalculate totals based on refreshed invoices
    const newTotalGenerated = {
      count: refreshedInvoices.length,
      totalAmount: refreshedInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0),
      paidCount: refreshedInvoices.filter(inv => inv.status === 'paid').length,
      paidAmount: refreshedInvoices
        .filter(inv => inv.status === 'paid')
        .reduce((sum, inv) => sum + (inv.amount || 0), 0),
      lastUpdated: new Date() // Use current date for lastUpdated
    };

    return {
      invoices: refreshedInvoices,
      totals: newTotalGenerated,
    };
  } catch (error) {
    // 'Error getting and refreshing template generated invoices:', error);
    // Fallback to stored data if refresh fails for some reason to prevent breaking the UI
    // but ideally, this error should be handled more gracefully or logged for investigation
    const templateRef = doc(db, 'invoiceTemplates', templateId);
    const templateSnap = await getDoc(templateRef); // Re-fetch, or pass templateData from above
    if (templateSnap.exists()) {
        const templateData = templateSnap.data();
        return {
            invoices: templateData.generatedInvoices || [],
            totals: templateData.totalGenerated || { count: 0, totalAmount: 0, paidCount: 0, paidAmount: 0, lastUpdated: new Date() }
        };
    }
    throw error; // Or return a default empty state
  }
}

/**
 * Handles payment processing for recurring invoices
 * Captures payment method on first payment and updates template tracking
 */
export async function handleRecurringInvoicePayment(
  invoiceId: string,
  transactionData: {
    transactionId: string;
    paymentDate: Date;
    paymentMethodId?: string;
    nmiCustomerId?: string;
    paymentMethodType?: 'credit_card' | 'ach';
    last4?: string;
    cardType?: string;
    expiryMonth?: string;
    expiryYear?: string;
    accountType?: string;
    routingNumberLast4?: string;
    nickName?: string;
  }
) {
  try {
    // Get the invoice to check if it's from a recurring template
    const invoiceRef = doc(db, 'invoices', invoiceId);
    const invoiceSnap = await getDoc(invoiceRef);
    
    if (!invoiceSnap.exists()) {
      throw new Error('Invoice not found');
    }
    
    const invoiceData = invoiceSnap.data();
    const templateId = invoiceData.metadata?.recurringTemplateId;
    
    if (!templateId) {
      // Not a recurring invoice, nothing to do
      return;
    }
    
    // Update the invoice status in template tracking
    await updateInvoiceStatusInTemplate(templateId, invoiceId, 'paid', {
      paymentDate: transactionData.paymentDate,
      transactionId: transactionData.transactionId
    });
    
    // Check if this is the first payment and we need to capture the payment method
    const templateRef = doc(db, 'invoiceTemplates', templateId);
    const templateSnap = await getDoc(templateRef);
    
    if (!templateSnap.exists()) {
      // 'Template not found for payment method capture');
      return;
    }
    
    const templateData = templateSnap.data();
    
    // If template doesn't have a stored payment method yet, and we have payment method data
    if (!templateData.storedPaymentMethod && transactionData.paymentMethodId && transactionData.nmiCustomerId) {
      // `Capturing payment method for template ${templateId} from first payment`);
      
      await updateTemplateWithPaymentMethod(templateId, {
        paymentMethodId: transactionData.paymentMethodId,
        nmiCustomerId: transactionData.nmiCustomerId,
        type: transactionData.paymentMethodType || 'credit_card',
        last4: transactionData.last4 || '',
        cardType: transactionData.cardType,
        expiryMonth: transactionData.expiryMonth,
        expiryYear: transactionData.expiryYear,
        accountType: transactionData.accountType,
        routingNumberLast4: transactionData.routingNumberLast4,
        nickName: transactionData.nickName || ''
      });
      
      // Also update all future invoices generated from this template to include the payment method
      const invoicesQuery = query(
        collection(db, 'invoices'),
        where('metadata.recurringTemplateId', '==', templateId),
        where('status', 'in', ['pending', 'sent', 'draft'])
      );
      
      const futureInvoicesSnap = await getDocs(invoicesQuery);
      const batch = writeBatch(db);
      
      futureInvoicesSnap.docs.forEach(doc => {
        batch.update(doc.ref, {
          storedPaymentMethod: {
            paymentMethodId: transactionData.paymentMethodId,
            nmiCustomerId: transactionData.nmiCustomerId,
            type: transactionData.paymentMethodType || 'credit_card',
            last4: transactionData.last4 || '',
            cardType: transactionData.cardType,
            expiryMonth: transactionData.expiryMonth,
            expiryYear: transactionData.expiryYear,
            accountType: transactionData.accountType,
            routingNumberLast4: transactionData.routingNumberLast4,
            nickName: transactionData.nickName || '',
            capturedAt: new Date()
          },
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
      // `Updated ${futureInvoicesSnap.docs.length} future invoices with payment method`);
    }
    
    // `Successfully processed recurring invoice payment for template ${templateId}`);
  } catch (error) {
    console.error('Error handling recurring invoice payment:', error);
    throw error;
  }
}

/**
 * Migration function to populate template tracking for existing invoices
 * that were generated from templates but not tracked
 */
export async function migrateExistingInvoicesToTemplateTracking() {
  try {
    // 'Starting migration of existing invoices to template tracking...');
    
    // Get all invoices that have recurring template metadata but might not be tracked
    const invoicesQuery = query(
      collection(db, 'invoices'),
      where('metadata.recurringTemplateId', '!=', null)
    );
    
    const invoicesSnapshot = await getDocs(invoicesQuery);
    // `Found ${invoicesSnapshot.docs.length} invoices with template references`);
    
    let migrationSuccessCount = 0;
    let migrationErrorCount = 0;
    
    for (const invoiceDoc of invoicesSnapshot.docs) {
      try {
        const invoiceData = invoiceDoc.data();
        const templateId = invoiceData.metadata?.recurringTemplateId;
        
        if (!templateId) {
          continue;
        }
        
        // Check if this invoice is already tracked in the template
        const templateRef = doc(db, 'invoiceTemplates', templateId);
        const templateSnap = await getDoc(templateRef);
        
        if (!templateSnap.exists()) {
          // `Template ${templateId} not found for invoice ${invoiceDoc.id}`);
          continue;
        }
        
        const templateData = templateSnap.data();
        const existingInvoices = templateData.generatedInvoices || [];
        
        // Check if this invoice is already tracked
        const isAlreadyTracked = existingInvoices.some((inv: any) => inv.invoiceId === invoiceDoc.id);
        
        if (!isAlreadyTracked) {
          // Add this invoice to the tracking
          await addInvoiceToTemplateTracking(templateId, {
            invoiceId: invoiceDoc.id,
            invoiceNumber: invoiceData.invoiceNumber,
            dueDate: invoiceData.dueDate || new Date().toISOString().split('T')[0],
            amount: invoiceData.total || 0,
            status: invoiceData.status || 'pending'
          });
          
          // `Migrated invoice ${invoiceDoc.id} to template ${templateId} tracking`);
          migrationSuccessCount++;
        }
      } catch (error) {
        logApiError('migrateTemplateInvoices.invoice', error);
        migrationErrorCount++;
      }
    }
    
    // `Migration completed. Success: ${migrationSuccessCount}, Errors: ${migrationErrorCount}`);
    return { success: migrationSuccessCount, errors: migrationErrorCount };
  } catch (error) {
    console.error('Error in migration:', error);
    throw error;
  }
}
